/*
 * <course-designer> — storefront course-overview generator.
 *
 * Flow (mirrors generation-api/web/static/index.html):
 *   1. Autocomplete: GET {api}/api/search?q=  (150ms debounce + AbortController)
 *   2. Generate:     POST {api}/api/course-overview -> title-less PNG + title box
 *   3. Title edit:   live SVG overlay in the Birthstone web font (no round-trip)
 *
 * No /api/prewarm anywhere in here — see selectSuggestion for why.
 *
 * The generated map is drawn over the product photo (the overlay element lives
 * in .product__media-wrapper). Design params are mirrored into hidden line-item
 * property inputs so the order captures a reproducible design.
 */
(function () {
  'use strict';

  var SVGNS = 'http://www.w3.org/2000/svg';

  // generation-API error codes that mean "not enough course data" rather than
  // "something broke". Only a fallback: the API also flags these with
  // detail.kind === 'course_data' (see isCourseDataError).
  // NB: the API no longer refuses a course for having gaps — it renders what it
  // has and reports them in `completeness` (the information box). Only a course
  // it can't draw at all still fails here.
  var COURSE_DATA_CODES = [
    'course_data_unavailable',  // legacy: pre-OSM-catalogue API builds only
    'insufficient_coverage',    // no usable OSM geometry (missing way/features)
    'course_not_found',         // course isn't in our data at all
    'ambiguous_course',         // can't pin the typed name to one course
    'hole_out_of_range',        // scorecard/geometry disagree on hole count
  ];

  // Title face — locked to Birthstone (the only font the storefront exposes).
  var FONT_FACE = { family: "'Birthstone', cursive", weight: '400', style: 'normal' };
  var LINE_GAP_FACTOR = 0.5; // Birthstone-specific, matches the API web UI.
  // Mirrors golfbroidery.course.TITLE_THIRD_LINE_FLOOR / TITLE_MAX_LINES. The
  // floor is a fraction of the em cap rather than millimetres, so it scales with
  // the box it is judging and there is no second absolute constant to drift.
  var THIRD_LINE_FLOOR = 0.5;
  var MAX_TITLE_LINES = 4;

  // Ceiling on one generate. A cold whole-club map legitimately takes ~40s
  // today, so this is a floor under the wait rather than a target: past it the
  // request is not coming back in a time anyone will sit through, and saying so
  // beats a spinner that never ends. Raise it only alongside the API's own
  // render times — aborting a request that would have landed costs a customer
  // their design and costs us the render twice.
  var GENERATE_TIMEOUT_MS = 45000;
  // When to admit it's slow. Comfortably past an ordinary render, so a normal
  // generate never shows this at all.
  var SLOW_NOTICE_MS = 12000;
  // Status-line copy for the two waiting states. Hardcoded like the designer's
  // other transient status strings ("Preparing your design…"): it's mechanical
  // feedback, not merchant-editable content. The copy the customer reads when
  // it actually fails is a theme setting (CourseTimeoutModal).
  var SLOW_NOTICE = 'Still working — this one is taking a little longer.';
  var TIMEOUT_FALLBACK = 'This is taking longer than usual. Please try again.';

  // The dock stands down the moment any part of the real gallery is on screen —
  // threshold 0, not a fraction. A fraction docks while the gallery is still
  // partly visible, which puts two copies of the same photo on the same screen;
  // the full-size one always wins.
  var DOCK_ANCHOR_THRESHOLD = 0;
  // How far a finger may travel and still count as a tap on the dock rather
  // than a swipe along it. Roughly a fingertip's wobble.
  var DOCK_TAP_SLOP = 8;
  // Visual viewport this much shorter than the layout viewport means the
  // on-screen keyboard is up, and the dock should give the field its room back.
  var KEYBOARD_VIEWPORT_RATIO = 0.75;

  // Courses this browser has designed before, offered back before the customer
  // types. Per-browser and nothing else: no account, no server, and the only
  // thing stored is what they already told us. Four is about what fits on a
  // phone without the panel becoming a list to read.
  var RECENT_KEY = 'tothenines:recent-courses';
  var RECENT_MAX = 4;
  // Whether this deployment's API answers /api/nearby. Cached for the session
  // so the probe is one request, not one per focus. The endpoint is newer than
  // this theme, so the button has to earn its place rather than assume it.
  // Shared with the homepage hero (hero-course-search.js) on purpose: one probe
  // answers for both surfaces.
  var NEARBY_KEY = 'tothenines:nearby-supported';

  // "Tell me when it's mapped" (course-notify-form.liquid). The email is kept
  // per-browser only to fill the field next time — someone who has already
  // given it once shouldn't have to type it for the next unfinished course.
  // The pending entry lives in sessionStorage just across a native form post:
  // it carries the course over the reload so the design comes back, and says
  // the confirmation belongs to that course.
  var NOTIFY_EMAIL_KEY = 'tothenines:notify-email';
  var NOTIFY_PENDING_KEY = 'tothenines:notify-pending';
  var NOTIFY_PENDING_TTL_MS = 10 * 60 * 1000;
  // Shopify customer tags cap at 255 characters and split on commas.
  var NOTIFY_TAG_MAX = 120;
  // How far out to look, widening a step at a time as the customer pulls past
  // the end of the list (course-nearby-more.js). The first figure is the API's
  // own default and is named here because the empty-result copy has to say it —
  // "no courses near you" invites the question this answers.
  //
  // The limit has to climb with the radius or the wider search returns the same
  // rows: the nearest ten within 250km ARE the nearest ten within 80km almost
  // everywhere. 25 is the API's ceiling, which is what caps this ladder at
  // three rungs — past that a wider radius only finds anything in a country
  // with very little golf in it, which is exactly who needs the last rung.
  var NEARBY_STEPS = [
    { radius_km: 80, limit: 10 },
    { radius_km: 250, limit: 25 },
    { radius_km: 500, limit: 25 },
  ];
  var NEARBY_RADIUS_KM = NEARBY_STEPS[0].radius_km;
  // The pull footer's three lines. The last one has to be careful: the ladder
  // running out means there is no more golf around the customer, not that there
  // are no more courses — the catalogue has the world in it, and a line that
  // sounded like the end of it would send somebody away from a course we can
  // draw. So it says what has run out and hands them the field, which is the
  // right tool for a course three counties from here.
  var NEARBY_MORE_LABELS = {
    more: 'Search further out',
    loading: 'Searching further out…',
    end: 'Nothing more nearby. Type a course name to find it.',
  };
  // The three ways this ends without a list, kept apart because they need
  // different things from the customer. Rolling them into one message (which is
  // what shipped first) told somebody whose location worked perfectly well that
  // we couldn't get their location.
  //
  // Hardcoded, like the designer's other mechanical status strings: these name
  // browser mechanics — a permission the store owner doesn't control and can't
  // usefully reword. The button's own label is a theme setting.
  var NEARBY_LOCATING = 'Finding your location…';
  var NEARBY_DENIED =
    'Location is off for this site. Turn it on in your browser, or type the course name.';
  var NEARBY_NO_FIX = "Couldn't get a location fix. Try again, or type the course name.";
  // What the list says it is once it's on screen. Not a search result — a
  // ranking, and the customer needs to know which.
  var NEARBY_HEADING = 'Nearest to you';

  // An /api/search hit's course name with no location attached. The API's
  // `label` field is the display string ("Name (City, Country)") and `place`
  // the location on its own — neither belongs in an input or an order.
  //
  // `name`/`place` only exist on API builds from the location-annotation change
  // onward. Older builds (which is what production runs until that branch is
  // merged) send `label` + `club_name` + `course_name` only, and there
  // `course_name` is the *sub-course* on its own — "Old", "New", "West Course",
  // "Sunningdale Ladies". Falling through to it would put "Old" in the search
  // box and on the customer's order, so reconstruct the club/course join the
  // newer API sends as `name` instead. Mirrors meta._base_label.
  function courseNameOf(item) {
    if (item.name) return item.name;
    var club = item.club_name || '';
    var course = item.course_name || '';
    if (club && course && club !== course) return club + ' — ' + course;
    if (club || course) return club || course;
    return item.label || '';
  }

  // Where the course is, for the dropdown's second line. Newer API builds send
  // it as `place`; older ones only glue it onto the tail of `label` as
  // "(City, Region, Country)", so recover it from there.
  //
  // Only trust those trailing parentheses when stripping them leaves exactly
  // the reconstructed name — otherwise a club that genuinely ends in brackets
  // would have part of its own name shown as a location.
  function coursePlaceOf(item) {
    if (item.place) return item.place;
    if (item.name) return ''; // new-shape result that simply has no location
    var label = item.label || '';
    var match = /\s\(([^()]*)\)$/.exec(label);
    if (!match) return '';
    return label.slice(0, match.index) === courseNameOf(item) ? match[1] : '';
  }

  // ---- How far away a course is -------------------------------------------
  //
  // The catalogue sorts the nearby list by distance and sends every hit's
  // coordinates, so the number the order was made from can be shown without
  // asking the API for it. Showing it is what makes the list legible as a
  // ranking rather than as a second, mysteriously-ordered search — and it is
  // the question a golfer asks about anywhere they might play.

  // Miles where the customer's own browser says they measure roads in miles,
  // kilometres everywhere else. Read from the language tags rather than from
  // the coordinate: somebody from Chicago on a golf trip to Fife still thinks
  // in miles, and the tags are what say so.
  function usesMiles() {
    var tags = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || ''];
    for (var i = 0; i < tags.length; i++) {
      if (/-(GB|US|MM|LR)\b/i.test(tags[i])) return true;
    }
    return false;
  }

  var KM_PER_MILE = 1.609344;
  var EARTH_RADIUS_KM = 6371;

  function distanceKm(lat1, lon1, lat2, lon2) {
    var rad = Math.PI / 180;
    var dLat = (lat2 - lat1) * rad;
    var dLon = (lon2 - lon1) * rad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
  }

  // "2.4 mi" round the corner, "27 mi" across the county. A tenth of a mile
  // means something when the course is close and is noise when it is half an
  // hour away.
  function formatDistance(km) {
    var miles = usesMiles();
    var value = miles ? km / KM_PER_MILE : km;
    var unit = miles ? 'mi' : 'km';
    return (value < 10 ? value.toFixed(1) : String(Math.round(value))) + ' ' + unit;
  }

  // The search radius, spelled out for the sentence that has to say it.
  function radiusLabel() {
    return usesMiles()
      ? Math.round(NEARBY_RADIUS_KM / KM_PER_MILE) + ' miles'
      : NEARBY_RADIUS_KM + ' kilometres';
  }

  // A hit's coordinates, where the API sent them. Older builds and the
  // GolfCourseAPI fallback don't, and a row without them simply shows no
  // distance rather than showing a wrong one.
  function coordsOf(item) {
    var loc = item && item.location;
    if (!loc) return null;
    var lat = typeof loc.latitude === 'number' ? loc.latitude : null;
    var lon = typeof loc.longitude === 'number' ? loc.longitude : null;
    return lat === null || lon === null ? null : { lat: lat, lon: lon };
  }

  // Shared, always-rendered measuring surface for the title fit.
  //
  // getComputedTextLength() only answers in a laid-out SVG, and the title is
  // painted into several overlays — the product-gallery slide, the overview
  // slide, the cart-upsell and coming-soon mirrors — any of which can be
  // `hidden` at paint time. Measuring in the target would then return 0 and the
  // title would be drawn at its cap instead of its fitted size. So measure here
  // instead: visibility:hidden still produces layout (display:none would not),
  // and one surface for every overlay also guarantees they agree with each other.
  var _measureSvg = null;
  function measuringSvg() {
    if (_measureSvg && _measureSvg.isConnected) return _measureSvg;
    _measureSvg = document.createElementNS(SVGNS, 'svg');
    _measureSvg.setAttribute('aria-hidden', 'true');
    _measureSvg.style.cssText =
      'position:absolute;left:-9999px;top:0;width:1px;height:1px;visibility:hidden;overflow:hidden';
    document.body.appendChild(_measureSvg);
    return _measureSvg;
  }

  // Mirror golfbroidery.course._title_lines. The customer's own breaks (Enter in
  // the title field) win outright — a break placed on purpose is never re-flowed
  // — otherwise a title of more than two words wraps its last two onto line two.
  // fit_title_size may add a third line on top of this; see paintTitle.
  function hasExplicitBreaks(text) {
    return /[\r\n]/.test(text || '');
  }

  function explicitTitleLines(text) {
    var lines = String(text || '')
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map(function (l) { return l.trim(); })
      .filter(Boolean)
      .slice(0, MAX_TITLE_LINES);
    return lines.length ? lines : null;
  }

  function wrapLastTwo(name) {
    var words = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length > 2) {
      return [words.slice(0, -2).join(' '), words.slice(-2).join(' ')];
    }
    return [String(name || '').trim()];
  }

  function titleLines(name) {
    return (hasExplicitBreaks(name) && explicitTitleLines(name)) || wrapLastTwo(name);
  }

  function ensureTitleFont() {
    if (!document.fonts) return Promise.resolve();
    return document.fonts
      .load("400 40px 'Birthstone'")
      .then(function () { return document.fonts.ready; })
      .catch(function () { /* fall back to whatever is available */ });
  }

  // Below this, the gallery sits above the product info instead of beside it
  // (see .grid--2-col-tablet in base.css) — the same breakpoint media-gallery.js
  // uses to decide mobile vs desktop scroll behaviour.
  function isMobile() {
    return !window.matchMedia('(min-width: 750px)').matches;
  }

  // Read at call time, not once: the setting can change under a running page.
  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ---- Carrying a design between products --------------------------------
  //
  // A design belongs to the customer, not to the garment it was made on. Someone
  // who designs their course on a tee and then follows a suggested-product link
  // to the sweatshirt means to see the same course on the sweatshirt, not the
  // placeholder again. A colour swap keeps the design because it replaces the
  // gallery in place; a navigation is a whole new document, so the design has to
  // be handed over through storage.
  //
  // What travels is the design itself — the hosted map per ink, the title box
  // geometry and the customer's title text — never the flattened proof. The new
  // page repaints it as the same two live layers (map <img> + title <svg>) it
  // would have drawn itself, so the title stays editable on the new garment.
  //
  // sessionStorage rather than localStorage: this is "the design I am working on
  // now". It should follow the customer through the shop and then be gone, not
  // resurrect on a visit next month pointing at map URLs that have since expired.
  var STORE_KEY = 'ttn:course-design:v1';
  // A browser that restores a session can hand back state from days ago; past
  // this, start clean rather than repaint a design the customer has forgotten.
  var STORE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

  // Both guarded: storage throws outright in Safari's private mode and when the
  // quota is full, and a carried design is never worth breaking the page for.
  function readSavedDesign() {
    var raw;
    try { raw = window.sessionStorage.getItem(STORE_KEY); }
    catch (_) { return null; }
    if (!raw) return null;
    var data;
    try { data = JSON.parse(raw); }
    catch (_) { return null; }
    if (!data || data.v !== 1) return null;
    if (!data.savedAt || Date.now() - data.savedAt > STORE_MAX_AGE_MS) return null;
    return data;
  }

  function writeSavedDesign(data) {
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(data)); }
    catch (_) { /* private mode / quota — the design just doesn't travel */ }
  }

  class CourseDesigner extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
      this.requireDesign = this.dataset.requireDesign === 'true';
      this.titleFont = this.dataset.titleFont || 'birthstone';
      // Ink scheme the map is drawn in. Starts from this product's metafield and
      // follows the selected garment colour (each swatch carries its own value),
      // so a pale garment gets a dark map and a dark garment a light one.
      this.courseInk = this.dataset.courseInk || 'green';
      // The garment's own colour, used as the overview field so the art previews
      // on the actual garment shade. Follows the selected swatch on colour swap.
      // Falls back to the ink-contrast pair when a product has no swatch set.
      this.swatchColor = this.dataset.swatchColor || '';
      // Placeholder course-overview art per ink scheme ("Your Golf Course Here"),
      // shown on the overlay + overview slide until the customer generates their
      // first design, then swapped for the real map.
      this.placeholder = {
        green: this.dataset.placeholderGreen || '',
        cream: this.dataset.placeholderCream || '',
      };
      this.designToken = null;
      // A title carried from another garment, applied to the next design this
      // page renders instead of the API's default (see restoreSavedDesign).
      this._pendingTitle = null;
      // Pre-rendered map per ink scheme: { green: {src, url, title_ink}, ... }.
      // Populated on generate so a colour swap is an instant client-side <img>
      // swap with no round-trip (see recolor).
      this.variants = null;

      this.searchEl = this.q('search');
      this.suggestionsEl = this.q('suggestions');
      // What is offered before anything is typed, and what is said when a
      // search finds nothing.
      this.entryEl = this.q('entry');
      this.nearbyEl = this.q('nearby');
      // The button's own label, swapped for the pending state, and the line
      // under it that says why there is no list. Both optional: the coming-soon
      // designer renders its own markup without them.
      this.nearbyLabelEl = this.q('nearby-label');
      this.nearbyNoteEl = this.q('nearby-note');
      this.recentEl = this.q('recent');
      this.recentChipsEl = this.q('recent-chips');
      this.noResultsEl = this.q('no-results');
      this.noResultsReportEl = this.q('no-results-report');
      this.titleSuggestionsEl = this.q('title-suggestions');
      this.titleChipsEl = this.q('title-chips');
      this.titleFieldEl = this.q('title-field');
      this.titleEl = this.q('title');
      this.generateEl = this.q('generate');
      this.statusEl = this.q('status');
      this.propCourse = this.q('prop-course');
      this.propTitle = this.q('prop-title');
      this.propCourseId = this.q('prop-course-id');
      // Multi-course clubs: the picker container, the note under it, and the two
      // line-item properties (customer-facing name + machine index).
      this.routingFieldEl = this.q('routing-field');
      this.routingEl = this.q('routing');
      this.routingNoteEl = this.q('routing-note');
      this.propRouting = this.q('prop-routing');
      this.propRoutingName = this.q('prop-routing-name');
      this.propCourseInk = this.q('prop-course-ink');
      // What the API could and couldn't draw for this course, captured for
      // fulfillment (which courses need finishing by hand before they're made).
      this.propCompleteness = this.q('prop-completeness');

      // "About your preview" info box (course-completeness.liquid). Absent on a
      // theme that hasn't added the snippet — every use is guarded.
      this.noticeEl = this.q('completeness');
      this.noticePartialEl = this.q('completeness-partial');
      this.noticeListEl = this.q('completeness-list');
      this.noticeCompleteEl = this.q('completeness-complete');
      // Hosted map URL per ink scheme, captured as line-item properties so
      // fulfillment (and a future "my designs") can reuse the asset.
      this.propMap = { green: this.q('prop-map-green'), cream: this.q('prop-map-cream') };
      // Flattened cart proof (garment + titled map) URL line-item property.
      this.propProof = this.q('prop-proof');
      // Title-box geometry ("left,right,cy,h,canvasW,canvasH" in canvas mm) so the
      // cart can re-bake this design on a sibling garment without the live token.
      this.propGeom = this.q('prop-geom');

      // Feedback / reporting: the two trigger buttons plus copy for the report
      // modal's per-type intro. The modals themselves (CourseDataModal +
      // ReportModal) are rendered by report-modal.liquid and reused via the
      // theme's <modal-dialog> element; they move themselves to <body>, so we
      // resolve them by id when opening rather than caching the nodes here.
      this.reportIssueBtn = this.q('report-issue');
      this.missingCourseBtn = this.q('missing-course');
      this.reportIntro = {
        generation_issue: this.dataset.reportIntroIssue || '',
        missing_course: this.dataset.reportIntroMissing || '',
      };
      this.reportLabel = {
        generation_issue: this.dataset.reportIssueLabel || '',
        missing_course: this.dataset.missingCourseLabel || '',
      };

      // Overlay placement on the product photo (block settings), forwarded to
      // /api/course-proof so the server bakes the composite at the same position.
      this.overlayTopPct = parseFloat(this.dataset.overlayTopPct) || 0;
      this.overlayLeftPct = parseFloat(this.dataset.overlayLeftPct) || 0;
      this.overlayWidthPct = parseFloat(this.dataset.overlayWidthPct) || 50;
      // Add-to-cart serialization: `_adding` ignores re-entrant submits while a
      // proof bake + add is in flight; `_bypassProof` is the one-shot flag set
      // right before we re-fire submit so the resubmit passes straight through to
      // <product-form> instead of re-baking (see onSubmit).
      this._adding = false;
      this._bypassProof = false;

      // The map src currently shown, retained so the design can be repainted onto
      // freshly re-rendered surfaces after a colour swap replaces the gallery.
      this.currentMapSrc = null;

      // Overlay (first image) + the full-overview slide (both inside the gallery)
      // and the submit button (in buy-buttons).
      this.acquireMedia();
      this.submitEl = document.getElementById(this.dataset.submitId);
      // The sticky mobile buy bar, rendered outside this element (and outside
      // the animated info column — see the snippet for why).
      this.acquireBuyBar();

      this.selectedCourseId = null;
      // OSM course id from the catalogue-backed autocomplete, when the API is
      // serving one. Preferred over course_id/name: it resolves the course from
      // the row its coordinates came from, with no name matching in between.
      this.selectedOsmId = null;
      // Which course at a multi-course club: 0, 1, … or the string "all".
      // Null at an ordinary club, where there is nothing to pick.
      this.selectedRouting = null;
      this._routings = [];
      this.routingAllLabel = this.dataset.routingAllLabel || 'All courses';
      this.routingAllNote = this.dataset.routingAllNote || '';
      // The last `completeness` payload, retained so a colour swap (which keeps
      // the same design) doesn't drop the notice.
      this.completeness = null;
      this.titleCtx = null;
      this._acDebounce = null;
      this._acAbort = null;
      this._acItems = [];
      this._acActive = -1;

      this.gateSubmit(false);
      this.bind();
      // Keyboard / orientation / breakpoint changes all move the mobile dock.
      this.watchViewport();
      // No design yet: present the placeholder course overview for this product's
      // ink on both the overlay and the standalone overview slide.
      this.showPlaceholder();
      // Back from a native notify-me post: put the course they signed up for
      // into the URL so the line below designs it again.
      this.bindNotifyForms();
      this.bindShareButtons();
      this.restoreNotifyState();
      // …unless the customer arrived from the homepage search already knowing
      // which course they want, in which case design it straight away — or is
      // carrying a design they made on another garment, which is repainted onto
      // this one. The URL wins: it is an explicit, fresh choice.
      if (!this.applyIncomingCourse()) this.restoreSavedDesign();

      // A colour swap replaces the buy buttons (and the submit button) in place
      // and updates the active swatch; re-apply the current gate to the freshly
      // inserted button and recolour the map to match the new garment colour.
      this._onColorSwap = function () {
        // The swap replaced the whole gallery, so the overlay and overview slide
        // are fresh, empty elements — re-acquire them and repaint the retained
        // design before recolouring to the newly selected garment ink.
        this.acquireMedia();
        this.gateSubmit(this._hasDesign === true);
        var active = document.querySelector('.color-swatch[aria-current="page"]');
        var ink = (active && active.dataset.courseInk) || this.courseInk;
        // Track the newly selected garment colour so the overview field repaints
        // to match it (setOverviewBg reads this.swatchColor).
        if (active && active.dataset.swatchColor) this.swatchColor = active.dataset.swatchColor;
        if (this._hasDesign === true) {
          if (this.titleCtx) this.repaintDesign();
          this.recolor(ink);
        } else {
          // Still on the placeholder — just repaint it in the new garment's ink.
          this.courseInk = ink;
          if (this.propCourseInk) this.propCourseInk.value = ink;
          this.showPlaceholder();
        }
        // The swap kept the dock and gave it the new garment's slides, with the
        // design repainted onto them; it just needs telling whether to be on
        // screen.
        this.updateDock();
        // The buy bar survives the swap, but the button it watches and forwards
        // to does not: re-point the observer at the freshly inserted one.
        this.observeBuyBar();
        this.syncBuyBar();
        // The garment photo + ink changed, so any prior proof is stale. It's
        // re-baked fresh from the live garment + ink at add-to-cart (see
        // onSubmit), so just clear the stale value here.
        this.setProofProp('');
      }.bind(this);
      document.addEventListener('color-swatch:swapped', this._onColorSwap);

      // Bake the flattened proof before the product form submits. Captured at the
      // document so it keeps working after a colour swap replaces the form, and in
      // the capture phase so it runs before <product-form>'s own submit handler.
      this._onSubmit = this.onSubmit.bind(this);
      document.addEventListener('submit', this._onSubmit, true);
    }

    disconnectedCallback() {
      if (this._onColorSwap) {
        document.removeEventListener('color-swatch:swapped', this._onColorSwap);
      }
      if (this._onSubmit) {
        document.removeEventListener('submit', this._onSubmit, true);
      }
      if (this._onViewportChange) {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this._onViewportChange);
        }
        window.removeEventListener('resize', this._onViewportChange);
      }
      if (this._dockObserver) {
        this._dockObserver.disconnect();
        this._dockObserver = null;
      }
      if (this._buyObserver) {
        this._buyObserver.disconnect();
        this._buyObserver = null;
      }
      if (this._submitObserver) {
        this._submitObserver.disconnect();
        this._submitObserver = null;
      }
      if (this._priceObserver) {
        this._priceObserver.disconnect();
        this._priceObserver = null;
      }
      // Both bars are going with it; don't leave the page padded for either.
      document.documentElement.style.scrollPaddingTop = '';
      document.body.classList.remove('has-course-buy-bar');
    }

    q(ref) { return this.querySelector('[data-ref="' + ref + '"]'); }

    // (Re)acquire the gallery-internal design surfaces: the overlay pinned to the
    // first image and the standalone full-overview slide. Both live inside
    // <media-gallery>, which a colour swap replaces wholesale, so this runs on
    // connect and again after each swap (then the design is repainted from
    // retained state — see onColorSwap → repaintDesign).
    acquireMedia() {
      var overlay = document.getElementById(this.dataset.overlayId);
      this.overlayEl = overlay;
      this.mapEl = overlay ? overlay.querySelector('[data-ref="map"]') : null;
      this.titleSvg = overlay ? overlay.querySelector('[data-ref="title-svg"]') : null;

      var slide = document.querySelector('[data-ref="overview-slide"]');
      this.overviewSlideEl = slide;
      this.overviewFrameEl = slide ? slide.querySelector('[data-ref="overview-frame"]') : null;
      this.overviewMapEl = slide ? slide.querySelector('[data-ref="overview-map"]') : null;
      this.overviewTitleSvg = slide ? slide.querySelector('[data-ref="overview-title-svg"]') : null;
      // The design slide's entry in the thumbnail strip (course-overview-
      // thumbnail.liquid) — the art without the title, which wouldn't read at
      // that size anyway.
      var thumb = document.querySelector('[data-ref="thumb-overview-frame"]');
      this.thumbOverviewFrameEl = thumb;
      this.thumbOverviewMapEl = thumb ? thumb.querySelector('[data-ref="thumb-overview-map"]') : null;
      this.acquireSatellitePanel();

      // The mobile dock — the gallery in miniature, rendered inside it and so
      // replaced along with it. It carries a second copy of both design
      // surfaces (the overlay on the garment, and the design on its own), which
      // is why every painting method works off the mirror lists rather than off
      // named elements. Its observer and its listeners are watching elements
      // that have just been thrown away, so both are rebuilt here.
      var dock = document.querySelector('[data-ref="dock"]');
      this.dockEl = dock;
      this.dockTrackEl = dock ? dock.querySelector('[data-ref="dock-track"]') : null;
      this.dockMapEl = dock ? dock.querySelector('[data-ref="dock-map"]') : null;
      this.dockTitleSvg = dock ? dock.querySelector('[data-ref="dock-title-svg"]') : null;
      this.dockOverviewSlideEl = dock ? dock.querySelector('[data-ref="dock-overview-slide"]') : null;
      this.dockOverviewFrameEl = dock ? dock.querySelector('[data-ref="dock-overview-frame"]') : null;
      this.dockOverviewMapEl = dock ? dock.querySelector('[data-ref="dock-overview-map"]') : null;
      this.dockOverviewTitleSvg = dock ? dock.querySelector('[data-ref="dock-overview-title-svg"]') : null;
      this.bindDock();
      this.observeDock();
      this.updateDock();
      // A colour swap brings a fresh dock with its note hidden again; the
      // design (and what it's missing) carries over.
      this.syncPreviewNotes();
    }

    // The real gallery and its slides, which the dock mirrors one for one — the
    // dock's slides are captured from the same loops that build these, so index
    // n in one is index n in the other.
    galleryEl() {
      return this.overlayEl ? this.overlayEl.closest('media-gallery') : null;
    }

    gallerySliderEl() {
      var gallery = this.galleryEl();
      return gallery ? gallery.querySelector('.product__media-list') : null;
    }

    // The map <img>s and title <svg>s to keep in lockstep: the overlay on the
    // garment, the standalone overview slide, and the mobile dock. Each is
    // optional — a product page may render any subset of the three.
    mapImgs() {
      return [
        this.mapEl, this.overviewMapEl, this.dockMapEl, this.dockOverviewMapEl, this.thumbOverviewMapEl,
      ].filter(Boolean);
    }

    titleSvgs() {
      return [
        this.titleSvg, this.overviewTitleSvg, this.dockTitleSvg, this.dockOverviewTitleSvg,
      ].filter(Boolean);
    }

    // The mirrors mounted on a photograph of the garment, as against the
    // overview slide's flat field of colour. Only these want the multiply blend
    // that drops a generated map's white background onto the fabric.
    garmentMapImgs() { return [this.mapEl, this.dockMapEl].filter(Boolean); }

    revealOverview() {
      [this.overviewSlideEl, this.dockOverviewSlideEl].forEach(function (el) {
        if (el) el.hidden = false;
      });
    }

    // ---- Mobile design dock ------------------------------------------------
    // On a phone the gallery stacks above the product info, so the customer
    // reaches the designer with the garment off the top of the screen — picking
    // a colour, a size and a course against a photo they cannot see. The dock
    // is the gallery in miniature, pinned to the top of the viewport: the same
    // slides in the same order, swipeable the same way, carrying the same live
    // map and title (see mapImgs / titleSvgs).
    //
    // It replaces a scroll-to-top that used to fire when a design landed. That
    // put the design on screen by moving the customer away from the controls —
    // and it fired at exactly the moment the title field became editable, so
    // the field they were meant to use next ended up two viewports below them.
    // The dock shows the same thing without moving anybody.

    // Watch the whole gallery, not the one slide the overlay is pinned to, and
    // dock only once none of it is left on screen. Watching a single slide at a
    // fraction of its height slid the dock in over a gallery that was still
    // partly visible: two copies of the same photograph on one screen, one of
    // them small. Rebuilt on every acquireMedia — a colour swap replaces the
    // gallery, so the previously observed element is detached and would never
    // intersect again.
    observeDock() {
      if (this._dockObserver) {
        this._dockObserver.disconnect();
        this._dockObserver = null;
      }
      // Assume visible until told otherwise, so a browser without
      // IntersectionObserver simply never docks rather than docking always.
      // A colour swap re-runs this with the page where it was, so it keeps
      // what it last knew; resetting it undocked the strip for a frame.
      if (this._anchorVisible === undefined || !('IntersectionObserver' in window)) {
        this._anchorVisible = true;
      }
      if (!this.dockEl || !('IntersectionObserver' in window)) return;
      var anchor = this.gallerySliderEl() || this.galleryEl();
      if (!anchor) return;
      this._dockObserver = new IntersectionObserver(function (entries) {
        this._anchorVisible = entries[entries.length - 1].isIntersecting;
        this.updateDock();
      }.bind(this), { threshold: DOCK_ANCHOR_THRESHOLD });
      this._dockObserver.observe(anchor);
    }

    // In whenever the gallery has gone and we are on a phone — design or no
    // design. Gating this on a generated design was wrong: the customer spends
    // the first half of this page choosing a garment and a colour, and those
    // decisions want the photograph as much as the design does.
    updateDock() {
      if (!this.dockEl) return;
      var show = isMobile() && this._anchorVisible === false;
      var was = this.dockEl.classList.contains('is-docked');
      this.dockEl.classList.toggle('is-docked', show);
      // Arriving: open on whichever slide the gallery was left on, so the strip
      // continues what the customer was looking at rather than resetting.
      if (show && !was) this.syncDockToGallery();
      // Keep whatever the browser scrolls to next — the title field above all,
      // which the customer taps straight after a design lands — clear of a bar
      // that is fixed over the top of the page.
      document.documentElement.style.scrollPaddingTop = show
        ? this.dockEl.offsetHeight + 'px'
        : '';
    }

    // ---- Dock and gallery, one strip to the other --------------------------
    // The two carry the same slides in the same order — the dock's are captured
    // from the loops that build the gallery's — so a position in one is an
    // index the other understands. Measured off live rects rather than offsets:
    // both are scroll containers whose children are not positioned against
    // them, so offsetLeft answers about the wrong box.

    slidesOf(container) {
      if (!container) return [];
      var sel = container === this.dockTrackEl ? '.course-dock__slide' : ':scope > li';
      return Array.prototype.slice.call(container.querySelectorAll(sel));
    }

    // Which slide is under the middle of its strip right now.
    currentIndex(container) {
      var box = container.getBoundingClientRect();
      var mid = box.left + box.width / 2;
      var best = 0;
      var bestDist = Infinity;
      this.slidesOf(container).forEach(function (slide, i) {
        if (slide.hidden) return;
        var r = slide.getBoundingClientRect();
        if (!r.width) return;
        var dist = Math.abs(r.left + r.width / 2 - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    // Centre slide n in its strip. Horizontal only: scrollTo moves the strip,
    // where scrollIntoView would take the page with it.
    scrollStripTo(container, index, smooth) {
      if (!container) return;
      var slide = this.slidesOf(container)[index];
      if (!slide || slide.hidden) return;
      var box = container.getBoundingClientRect();
      var r = slide.getBoundingClientRect();
      var delta = r.left + r.width / 2 - (box.left + box.width / 2);
      if (Math.abs(delta) < 1) return;
      container.scrollTo({
        left: container.scrollLeft + delta,
        behavior: smooth && !reducedMotion() ? 'smooth' : 'auto',
      });
    }

    syncDockToGallery() {
      var slider = this.gallerySliderEl();
      if (!slider || !this.dockTrackEl) return;
      this.scrollStripTo(this.dockTrackEl, this.currentIndex(slider), false);
    }

    // Tap the dock, get the real thing — at the slide the dock is showing, so
    // swiping the strip and then tapping it lands on the photo that was chosen
    // rather than back at the first one.
    returnToGallery() {
      var gallery = this.galleryEl();
      var slider = this.gallerySliderEl();
      if (slider && this.dockTrackEl) {
        this.scrollStripTo(slider, this.currentIndex(this.dockTrackEl), false);
      }
      if (!gallery) return;
      gallery.scrollIntoView({
        behavior: reducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    // A drag scrolls the strip; a tap goes back up. Told apart by how far the
    // pointer travelled and whether the strip moved under it — a flick that
    // ends where it started still leaves the strip mid-scroll, and must not be
    // read as a tap. Called on every acquireMedia, but a colour swap now keeps
    // the same dock element (color-swatch-picker.js keepDock), so it binds only
    // once per element.
    bindDock() {
      if (!this.dockEl || this.dockEl._dockBound) return;
      this.dockEl._dockBound = true;
      var origin = null;
      this.dockEl.addEventListener('pointerdown', function (e) {
        origin = {
          x: e.clientX,
          y: e.clientY,
          scroll: this.dockTrackEl ? this.dockTrackEl.scrollLeft : 0,
          note: !!(e.target.closest && e.target.closest('[data-ref="dock-note"]')),
        };
      }.bind(this));
      this.dockEl.addEventListener('pointermove', function (e) {
        if (!origin) return;
        if (
          Math.abs(e.clientX - origin.x) > DOCK_TAP_SLOP ||
          Math.abs(e.clientY - origin.y) > DOCK_TAP_SLOP
        ) {
          origin = null;
        }
      });
      // The browser takes the pointer over for a scroll gesture and cancels it.
      this.dockEl.addEventListener('pointercancel', function () {
        origin = null;
      });
      this.dockEl.addEventListener('pointerup', function () {
        if (!origin) return;
        var moved = this.dockTrackEl
          ? Math.abs(this.dockTrackEl.scrollLeft - origin.scroll) > 1
          : false;
        var note = origin.note;
        origin = null;
        if (moved) return;
        // The note under the strip is about the design, not a way back to it.
        if (note) this.showPreviewNotice();
        else this.returnToGallery();
      }.bind(this));
    }

    // ---- Sticky mobile buy bar ---------------------------------------------
    // The add-to-cart on this page sits below a configurator, a colour picker
    // and a size picker, which on a phone puts it well past the fold for the
    // whole time the customer is designing. The bar keeps the price and one
    // action in thumb reach, and what that action is depends on whether there
    // is a design yet: before, it is the way to the search field; after, it is
    // the add-to-cart and forwards its click to the real submit button.
    //
    // Nothing here submits anything. The product form owns the variant id and
    // the line-item properties, and this must never become a second opinion.
    acquireBuyBar() {
      this.buyBarEl = document.querySelector('[data-ref="buy-bar"]');
      if (!this.buyBarEl) return;
      this.buyBarPriceEl = this.buyBarEl.querySelector('[data-ref="buy-bar-price"]');
      this.buyBarActionEl = this.buyBarEl.querySelector('[data-ref="buy-bar-action"]');
      this.buyBarLabelEl = this.buyBarEl.querySelector('[data-ref="buy-bar-label"]');
      this.buyBarSpinnerEl = this.buyBarEl.querySelector('.loading__spinner');
      // The store owner's pre-design copy, read once before the label starts
      // being overwritten with the real button's text.
      this.buyBarCtaLabel = this.buyBarLabelEl
        ? (this.buyBarLabelEl.textContent || '').trim()
        : '';
      if (this.buyBarActionEl) {
        this.buyBarActionEl.addEventListener('click', this.onBuyBarClick.bind(this));
      }
      this.mirrorPrice();
      this.observeBuyBar();
    }

    // Watch the real add-to-cart: the bar is for when that button isn't on
    // screen, and stands down the moment it is, so the customer is never
    // looking at two of the same control. Rebuilt after a colour swap, which
    // replaces the button with a fresh one.
    observeBuyBar() {
      if (this._buyObserver) {
        this._buyObserver.disconnect();
        this._buyObserver = null;
      }
      if (this._submitObserver) {
        this._submitObserver.disconnect();
        this._submitObserver = null;
      }
      var anchor = this.buyBarEl ? document.getElementById(this.dataset.submitId) : null;
      if (!anchor || !('IntersectionObserver' in window)) {
        // Nothing to watch. Answer "on screen", which keeps the bar down rather
        // than stranded over a page it has no way of following.
        this._buyAnchorVisible = true;
        return;
      }
      // Only the first observe assumes the button is on screen — that is what
      // stops the bar flashing in for the frame before the first callback. A
      // re-observe keeps the last answer: a colour or size change replaces the
      // button, and resetting here slid the bar away and back under the
      // customer's thumb every time they changed one.
      if (this._buyAnchorVisible === undefined) this._buyAnchorVisible = true;
      this._buyObserver = new IntersectionObserver(function (entries) {
        this._buyAnchorVisible = entries[entries.length - 1].isIntersecting;
        this.syncBuyBar();
      }.bind(this), { threshold: 0.5 });
      this._buyObserver.observe(anchor);

      // The bar wears the real button's state, and that state changes twice
      // during an add — our own gate while the proof bakes (see onSubmit) and
      // <product-form>'s while the cart-add posts — with nothing else calling
      // syncBuyBar in between. Watch the button directly so the bar keeps up.
      this._submitObserver = new MutationObserver(this.syncBuyBar.bind(this));
      this._submitObserver.observe(anchor, {
        attributes: true,
        attributeFilter: ['aria-disabled', 'disabled', 'class'],
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    // Copy the section's price into the bar and keep copying it. A size or
    // colour change rewrites that element in place, and the same edit is the
    // signal that the button's availability may have changed with it.
    mirrorPrice() {
      var priceEl = this.dataset.priceId
        ? document.getElementById(this.dataset.priceId)
        : null;
      if (!priceEl || !this.buyBarPriceEl) return;
      var sync = function () {
        this.buyBarPriceEl.innerHTML = priceEl.innerHTML;
        // A variant change rewrites this element and replaces the submit button
        // with it, which leaves the observer watching a node that is no longer
        // in the document and can never intersect again — the bar would then be
        // stuck showing or stuck hidden for the rest of the visit.
        this.observeBuyBar();
        this.syncBuyBar();
        // …and the note above that button came back hidden with it.
        this.syncPreviewNotes();
      }.bind(this);
      sync();
      this._priceObserver = new MutationObserver(sync);
      this._priceObserver.observe(priceEl, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    syncBuyBar() {
      if (!this.buyBarEl || !this.buyBarActionEl) return;
      var submit = document.getElementById(this.dataset.submitId);
      var hasDesign = this._hasDesign === true;
      // Once there is a design the bar IS the add-to-cart, so it says whatever
      // the real button says — "Sold out" included, which the theme owns.
      if (this.buyBarLabelEl) {
        this.buyBarLabelEl.textContent = hasDesign && submit
          ? (submit.textContent || '').trim()
          : this.buyBarCtaLabel;
      }
      // …and looks however it looks. An add from here is not instant: the proof
      // is baked against the generation API before the form posts, and both the
      // spinner and the status line that say so are in the product column,
      // which is off screen whenever this bar is up. Mirroring the busy state
      // is what makes the tap land visibly instead of appearing to do nothing
      // until the cart opens.
      var busy = hasDesign && !!submit && (
        submit.getAttribute('aria-disabled') === 'true' ||
        submit.classList.contains('loading')
      );
      this.buyBarActionEl.classList.toggle('loading', busy);
      if (this.buyBarSpinnerEl) this.buyBarSpinnerEl.classList.toggle('hidden', !busy);
      // Before a design the bar is never disabled: its job is to get the
      // customer to the field, not to present them with a dead button and
      // leave them to work out why.
      this.buyBarActionEl.disabled = hasDesign && submit ? submit.disabled : false;

      var show = isMobile() && this._buyAnchorVisible === false && !!submit;
      this.buyBarEl.classList.toggle('is-visible', show);
      // The page has to end above the bar, or its last screen is unreachable.
      document.body.classList.toggle('has-course-buy-bar', show);
      if (show) {
        document.documentElement.style.setProperty(
          '--course-buy-bar-h', this.buyBarEl.offsetHeight + 'px'
        );
      }
    }

    onBuyBarClick() {
      if (this._hasDesign === true) {
        var submit = document.getElementById(this.dataset.submitId);
        if (!submit || submit.disabled) return;
        // An add is already in flight — the bar is showing its spinner. Both
        // handlers downstream swallow the second click anyway; stopping here
        // keeps that a decision rather than a coincidence.
        if (submit.getAttribute('aria-disabled') === 'true') return;
        submit.click();
        return;
      }
      this.focusSearch();
    }

    // Put the keyboard on the course field and bring the field into view. The
    // focus has to happen here, inside the tap, or iOS won't raise the
    // keyboard at all. Scrolling to the middle of the screen beforehand used to
    // leave the field under the keyboard once it opened, since the keyboard
    // takes the bottom half. So the field is placed after the keyboard is up
    // (keepAboveKeyboard), and on a phone it goes to the top of what's left,
    // with the suggestions opening into the room below it.
    focusSearch() {
      if (!this.searchEl) return;
      if (!isMobile()) this.searchEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        this.searchEl.focus({ preventScroll: true });
      } catch (e) {
        this.searchEl.focus();
      }
      this.keepAboveKeyboard(this.searchEl, true);
    }

    // On a phone, keep a focused field in the strip of screen between the dock
    // and the keyboard. The keyboard doesn't resize the layout viewport, so this
    // works off the visual viewport, and it waits for that to shrink, as the
    // keyboard's height isn't known before then. `toTop` always lifts the field
    // to the top of the strip (the course search, whose suggestions need the
    // room); otherwise it only moves a field that is out of sight. Any scroll
    // the customer starts themselves ends it.
    keepAboveKeyboard(field, toTop) {
      if (!field || !isMobile()) return;
      var vv = window.visualViewport;
      var dock = this.dockEl;
      var done = false;
      var timers = [];

      var place = function () {
        if (done || document.activeElement !== field) return;
        var visTop = vv ? vv.offsetTop : 0;
        var visBottom = visTop + (vv ? vv.height : window.innerHeight);
        if (dock && dock.classList.contains('is-docked')) {
          visTop = Math.max(visTop, dock.getBoundingClientRect().bottom);
        }
        var rect = field.getBoundingClientRect();
        var gap = 12;
        var outOfSight = rect.top < visTop + gap || rect.bottom > visBottom - gap;
        if (!toTop && !outOfSight) return;
        var delta = rect.top - (visTop + gap);
        if (Math.abs(delta) > 2) window.scrollBy(0, delta);
      };

      var stop = function () {
        if (done) return;
        done = true;
        timers.forEach(clearTimeout);
        if (vv) vv.removeEventListener('resize', place);
        field.removeEventListener('blur', stop);
        window.removeEventListener('touchmove', stop);
      };

      // One at a time: the buy bar focuses the field, which fires the field's
      // own focus handler too.
      if (this._keyboardStop) this._keyboardStop();
      this._keyboardStop = stop;

      if (vv) vv.addEventListener('resize', place);
      field.addEventListener('blur', stop);
      window.addEventListener('touchmove', stop, { passive: true });
      // For keyboards that are already up, or browsers that never resize the
      // visual viewport; the second pass catches the dock compacting.
      timers.push(setTimeout(place, 350), setTimeout(place, 800), setTimeout(stop, 1500));
    }

    // The on-screen keyboard doesn't change the layout viewport, so CSS alone
    // can't tell that half the screen has gone. This is the moment the dock
    // matters most (the title is being typed), so it shrinks rather than hides.
    watchViewport() {
      this._onViewportChange = function () {
        if (!this.dockEl) return;
        var vv = window.visualViewport;
        if (vv) {
          this.dockEl.classList.toggle(
            'is-compact',
            vv.height < window.innerHeight * KEYBOARD_VIEWPORT_RATIO
          );
        }
        this.updateDock();
        this.syncBuyBar();
      }.bind(this);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this._onViewportChange);
      }
      // Orientation changes and the 750px breakpoint itself.
      window.addEventListener('resize', this._onViewportChange);
    }

    // The overview sits on the garment's own colour so the art previews on the
    // actual garment shade. The garment and ink are chosen to contrast, so the
    // map lines always read. Falls back to the ink-contrast pair (dark green map
    // on cream, light cream map on forest) when a product has no swatch set.
    setOverviewBg(ink) {
      var bg = this.swatchColor || (ink === 'cream' ? '#1F2D24' : '#EFE9DA');
      [this.overviewFrameEl, this.dockOverviewFrameEl, this.thumbOverviewFrameEl].forEach(function (el) {
        if (el) el.style.background = bg;
      });
    }

    placeholderSrc(ink) {
      return (ink === 'cream' ? this.placeholder.cream : this.placeholder.green) || '';
    }

    // Show the placeholder course overview ("Your Golf Course Here") for the
    // current ink on the overlay + overview slide. Used before the first design
    // and whenever the garment colour changes while no design exists. The title
    // is baked into the placeholder art, so the live title <svg>s are cleared.
    showPlaceholder() {
      var src = this.placeholderSrc(this.courseInk);
      if (this.overlayEl) this.overlayEl.hidden = false;
      this.revealOverview();
      if (src) this.mapImgs().forEach(function (img) { img.src = src; });
      // The placeholder PNG is transparent (unlike a generated map's white
      // background), so a normal blend reads correctly — including the light
      // "cream" ink on the dark garments, which a multiply blend would wash out.
      this.garmentMapImgs().forEach(function (img) { img.style.mixBlendMode = 'normal'; });
      this.titleSvgs().forEach(function (svg) {
        svg.innerHTML = '';
        svg.removeAttribute('viewBox');
      });
      this.setGeomProp();
      this.setOverviewBg(this.courseInk);
      this.clearSatellite();
      // No design yet → nothing to disclose about one, and nothing to retitle.
      this.clearCompleteness();
      if (this.titleSuggestionsEl) this.titleSuggestionsEl.hidden = true;
    }

    // Repaint the retained design onto freshly re-acquired (post-swap) surfaces:
    // reveal them, restore the last map + title-box, then let recolor() refine the
    // map to the new garment ink.
    repaintDesign() {
      if (!this.titleCtx) return;
      if (this.overlayEl) this.overlayEl.hidden = false;
      this.revealOverview();
      var viewBox = '0 0 ' + this.titleCtx.canvasW + ' ' + this.titleCtx.canvasH;
      if (this.currentMapSrc) {
        this.mapImgs().forEach(function (img) { img.src = this.currentMapSrc; }.bind(this));
      }
      this.titleSvgs().forEach(function (svg) { svg.setAttribute('viewBox', viewBox); });
      this.setOverviewBg(this.courseInk);
      this.applySatellite();
      ensureTitleFont().then(function () { this.renderTitle(); }.bind(this));
    }

    // ---- Satellite comparison ---------------------------------------------
    // A before/after wipe against the satellite view, in its own section under
    // the product (sections/course-accuracy.liquid) so the
    // gallery's design slide stays a plain design on the garment colour. The
    // panel shows the store's example until this customer has a design. The
    // API cuts the imagery to the design's own canvas — same frame, same
    // scale, north up — so it is stacked under the drawing at the same width
    // and nothing here does any fitting. The design side is the API's white
    // drawing of the same canvas over a blurred copy of the photo; white
    // because the photo runs from deep sea to sand and nothing else reads on
    // all of it. Both images are keyed on the render token, which a colour or
    // title change doesn't move; only a new design does. Best-effort
    // throughout: if anything fails, the panel keeps the example.

    // The panel sits outside the product section, so a colour swap never
    // replaces it; it is looked up again anyway in case the editor re-renders it.
    acquireSatellitePanel() {
      var panel = document.querySelector('[data-ref="design-compare-panel"]');
      if (panel === this.satellitePanelEl) return;
      this.satellitePanelEl = panel;
      this.satelliteCompareEl = panel ? panel.querySelector('[data-ref="design-compare"]') : null;
      this.satelliteUnderEl = panel ? panel.querySelector('.course-satellite__under') : null;
      this.satelliteBlurEl = panel ? panel.querySelector('[data-ref="design-satellite-blur"]') : null;
      this.satelliteOverEl = panel ? panel.querySelector('.course-satellite__map') : null;
      this.satelliteTitleSvg = panel ? panel.querySelector('[data-ref="design-satellite-title"]') : null;
      this.satelliteNoteEl = panel ? panel.querySelector('[data-ref="design-compare-note"]') : null;
      this.satelliteCreditEl = panel ? panel.querySelector('[data-ref="compare-credit"]') : null;
      // What the example looked like, so a reset can put it back exactly.
      var snap = function (img) {
        return img ? { src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), sizes: img.getAttribute('sizes'), alt: img.alt } : null;
      };
      this.satelliteExample = {
        under: snap(this.satelliteUnderEl),
        over: snap(this.satelliteOverEl),
        note: this.satelliteNoteEl ? this.satelliteNoteEl.textContent : '',
        credit: this.satelliteCreditEl ? this.satelliteCreditEl.textContent : '',
      };
      this._satelliteShown = null;
    }

    loadSatellite(token) {
      this.clearSatellite();
      if (!token || !this.apiBase || !this.satelliteUnderEl || !this.satelliteOverEl) return;
      var seq = (this._satelliteSeq = (this._satelliteSeq || 0) + 1);
      fetch(this.apiBase + '/api/course-satellite?token=' + encodeURIComponent(token))
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function (data) {
          // A newer design (or a reset) has started since; this one is stale.
          if (seq !== this._satelliteSeq || !data || !data.url) return;
          if (!data.overlay_url) return; // an API without the white drawing
          this.satellite = {
            src: this.resolveUrl(data.url),
            overlay: this.resolveUrl(data.overlay_url),
            credit: data.attribution || '',
          };
          this.applySatellite();
        }.bind(this))
        .catch(function (err) {
          if (window.console && console.info) console.info('[course-designer] no satellite view', err);
        });
    }

    // Paint the retained satellite cut into the panel. It only switches once
    // the photo and the white drawing have both loaded, so the panel never
    // shows one half of this design against the other half of the example.
    applySatellite() {
      this.acquireSatellitePanel();
      var sat = this.satellite;
      if (!sat || !this.satelliteUnderEl || !this.satelliteOverEl) return;
      if (this._satelliteShown === sat.src) return;
      var seq = this._satelliteSeq;
      var load = function (src) {
        return new Promise(function (resolve, reject) {
          var img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = src;
        });
      };
      Promise.all([load(sat.src), load(sat.overlay)]).then(function () {
        if (seq !== this._satelliteSeq) return;
        this.setPanelImg(this.satelliteUnderEl, { src: sat.src });
        this.setPanelImg(this.satelliteOverEl, { src: sat.overlay, alt: '' });
        if (this.satelliteBlurEl) this.satelliteBlurEl.src = sat.src;
        if (this.satelliteCreditEl) this.satelliteCreditEl.textContent = sat.credit;
        if (this.satelliteNoteEl && this.satelliteNoteEl.dataset.ready) {
          this.satelliteNoteEl.textContent = this.satelliteNoteEl.dataset.ready;
        }
        if (this.satelliteCompareEl && typeof this.satelliteCompareEl.set === 'function') {
          this.satelliteCompareEl.set(50);
        }
        this._satelliteShown = sat.src;
        if (this.satelliteTitleSvg) this.satelliteTitleSvg.hidden = false;
        ensureTitleFont().then(function () { this.renderTitle(); }.bind(this));
      }.bind(this)).catch(function () {
        if (window.console && console.info) console.info('[course-designer] satellite images failed to load');
      });
    }

    // The example images come with a srcset from image_tag, which would win
    // over a plain src; a live image drops it.
    setPanelImg(img, state) {
      if (!img || !state) return;
      ['srcset', 'sizes'].forEach(function (attr) {
        if (state[attr]) img.setAttribute(attr, state[attr]);
        else img.removeAttribute(attr);
      });
      if (state.src) img.setAttribute('src', state.src);
      else img.removeAttribute('src');
      if (state.alt != null) img.alt = state.alt;
    }

    // Back to the example: a new design is on its way, or there is none.
    clearSatellite() {
      this._satelliteSeq = (this._satelliteSeq || 0) + 1;
      this.satellite = null;
      if (!this._satelliteShown) return;
      this._satelliteShown = null;
      var ex = this.satelliteExample || {};
      this.setPanelImg(this.satelliteUnderEl, ex.under);
      this.setPanelImg(this.satelliteOverEl, ex.over);
      if (this.satelliteBlurEl) this.satelliteBlurEl.removeAttribute('src');
      if (this.satelliteTitleSvg) {
        this.satelliteTitleSvg.hidden = true;
        this.satelliteTitleSvg.innerHTML = '';
      }
      if (this.satelliteNoteEl) this.satelliteNoteEl.textContent = ex.note || '';
      if (this.satelliteCreditEl) this.satelliteCreditEl.textContent = ex.credit || '';
    }

    bind() {
      this.searchEl.addEventListener('input', this.onSearchInput.bind(this));
      this.searchEl.addEventListener('keydown', this.onSearchKeydown.bind(this));
      this.searchEl.addEventListener('blur', function () {
        // Delay so a suggestion click registers before the list hides — and so
        // a tap on Nearby or on a recent course lands before the panel holding
        // it goes away.
        setTimeout(function () {
          // A nearby run owns both of these until it has answered. The browser's
          // permission prompt takes the focus itself, so this blur is the
          // customer *using* the feature rather than leaving it, and 200ms later
          // there is nothing to show yet — the answer, list or line, would land
          // in a panel that had already closed underneath it.
          if (this._nearbyPinned) return;
          this.suggestionsEl.hidden = true;
          this.hideEntry();
        }.bind(this), 200);
      }.bind(this));
      if (this.generateEl) this.generateEl.addEventListener('click', this.onGenerate.bind(this));
      this.titleEl.addEventListener('input', this.onTitleInput.bind(this));

      // Before anything is typed, offer what we know: where they are, and what
      // they designed last time. Both stay hidden until they have something to
      // show, so an empty field usually still looks like an empty field.
      this.searchEl.addEventListener('focus', this.onSearchFocus.bind(this));
      // A tap straight on either field gets the same keyboard clearance as the
      // buy bar's "Create your design". iOS scrolls a focused field into view
      // itself, but it ignores the fixed dock and often stops with the field
      // behind the keyboard's toolbar.
      this.searchEl.addEventListener('focus', function () {
        this.keepAboveKeyboard(this.searchEl, true);
      }.bind(this));
      this.titleEl.addEventListener('focus', function () {
        this.keepAboveKeyboard(this.titleEl, false);
      }.bind(this));
      if (this.nearbyEl) {
        this.nearbyEl.addEventListener('click', this.onNearbyClick.bind(this));
      }
      if (this.noResultsReportEl) {
        this.noResultsReportEl.addEventListener('click', function () {
          this.openReport('missing_course');
        }.bind(this));
      }
      this.renderRecent();

      if (this.missingCourseBtn) {
        this.missingCourseBtn.addEventListener('click', function () {
          this.openReport('missing_course');
        }.bind(this));
      }
      if (this.reportIssueBtn) {
        this.reportIssueBtn.addEventListener('click', function () {
          this.openReport('generation_issue');
        }.bind(this));
      }

      // The generic error modal's contact button hands off to the report modal
      // as a design issue — course-data failures never reach this modal (they
      // get the apology modal below). This is the "email the store owner" path.
      var errorReport = document.querySelector('#CourseErrorModal [data-ref="error-modal-report"]');
      if (errorReport) {
        errorReport.addEventListener('click', function () {
          var em = document.getElementById('CourseErrorModal');
          if (em) em.hide();
          this.openReport(this._errorReportKind || 'generation_issue');
        }.bind(this));
      }

      // The timeout modal's only action: close it and run the identical
      // generate again. Nothing about the request needs to change — what timed
      // out was the wait, not the design — so this re-runs onGenerate rather
      // than resetting anything the customer chose.
      var timeoutRetry = document.querySelector('#CourseTimeoutModal [data-ref="timeout-modal-retry"]');
      if (timeoutRetry) {
        timeoutRetry.addEventListener('click', function () {
          var tm = document.getElementById('CourseTimeoutModal');
          if (tm) tm.hide();
          this.onGenerate();
        }.bind(this));
      }

      // The apology modal's "request this course" button hands off to the report
      // modal in missing-course mode (pre-filled with the typed course name).
      var dataReport = document.querySelector('#CourseDataModal [data-ref="data-modal-report"]');
      if (dataReport) {
        dataReport.addEventListener('click', function () {
          var dm = document.getElementById('CourseDataModal');
          if (dm) dm.hide();
          this.openReport('missing_course');
        }.bind(this));
      }

      // Report form: submits via fetch so the page never reloads (the generated
      // design survives) and the modal shows success inline. Requires hCaptcha
      // to be OFF for contact forms (admin → Preferences → Spam protection);
      // with it on, Shopify bounces the post to a /challenge interstitial that
      // can't complete behind the password page — we detect that and fall back
      // to a native submit as a last resort. The browser still enforces the
      // required email before the submit event fires.
      var reportForm = document.getElementById('ReportContactForm');
      if (reportForm) {
        reportForm.addEventListener('submit', this.onReportSubmit.bind(this));
      }
      var successClose = document.querySelector('#ReportModal [data-ref="report-success-close"]');
      if (successClose) {
        successClose.addEventListener('click', function () {
          var rm = document.getElementById('ReportModal');
          if (rm) rm.hide();
        });
      }

      this.restoreReportState();
    }

    // After a native contact-form post Shopify re-renders the page with the
    // form's status. If it posted successfully (or failed server-side), reopen
    // the report modal so the customer sees confirmation rather than a silently
    // reset page. Markers are rendered by report-modal.liquid.
    restoreReportState() {
      var modal = document.getElementById('ReportModal');
      if (!modal) return;
      var posted = modal.querySelector('[data-ref="report-posted"]');
      var serverError = modal.querySelector('[data-ref="report-server-error"]');
      if (!posted && !serverError) return;

      var focusEl = this.generateEl || this.searchEl;
      var open = function () {
        if (typeof modal.show !== 'function') return;
        var wrap = modal.querySelector('[data-ref="report-form-wrap"]');
        var success = modal.querySelector('[data-ref="report-success"]');
        if (posted) {
          // Confirmed sent: show the success panel.
          if (wrap) wrap.hidden = true;
          if (success) success.hidden = false;
        } else {
          // Server-side rejection (e.g. invalid email): keep the form visible
          // with the error (report-modal.liquid unhides it) so they can retry.
          if (wrap) wrap.hidden = false;
          if (success) success.hidden = true;
        }
        modal.show(focusEl);
      };

      // The <modal-dialog> custom element may not be upgraded yet at init; wait
      // for its definition so show() is available.
      if (typeof modal.show === 'function') {
        open();
      } else if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('modal-dialog').then(open);
      }
    }

    // AJAX-post the report form to Shopify's /contact endpoint. Success and
    // failure both render inside the modal; only a /challenge bounce (spam
    // protection still enabled) leaves the page, via the native-submit fallback.
    onReportSubmit(event) {
      // The challenge fallback below re-submits via requestSubmit(), which
      // fires this handler again — let that one through as a native post.
      if (this._reportNativePass) return;
      var form = event.currentTarget;
      // When hCaptcha is enabled, Shopify's captcha bootstrap owns this submit:
      // it listens at the document level on this same event, persists the form
      // under a form_key (which the verification page needs to resubmit), and
      // re-posts with a token attached. A fetch would race it and can never
      // carry the token (the server hard-400s "Missing CAPTCHA token"), so step
      // aside and let the native machinery run.
      if (form.dataset.hcaptchaBound || form.dataset.recaptchaBound || form.dataset.cptcha) {
        return;
      }
      event.preventDefault();
      var modal = document.getElementById('ReportModal');
      var submitBtn = form.querySelector('button[type="submit"]');
      var errorEl = modal ? modal.querySelector('[data-ref="report-error"]') : null;
      if (errorEl) errorEl.hidden = true;
      if (submitBtn) submitBtn.disabled = true;

      fetch(form.getAttribute('action') || '/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          Accept: 'text/html',
        },
        body: new URLSearchParams(new FormData(form)).toString(),
      })
        .then(function (response) {
          if (
            response.status === 400 ||
            (response.url && response.url.indexOf('/challenge') !== -1)
          ) {
            // Spam protection wanted a captcha token a fetch can never carry
            // (400 "Missing CAPTCHA token", or the legacy /challenge bounce).
            // Retry as a real submit: requestSubmit() fires the submit event so
            // Shopify's captcha bootstrap can persist the form and attach a
            // token (the _reportNativePass flag skips our own handler).
            // prototype.submit as a last resort because form controls can
            // shadow properties on the form element.
            this._reportNativePass = true;
            if (typeof form.requestSubmit === 'function') form.requestSubmit();
            else HTMLFormElement.prototype.submit.call(form);
            return;
          }
          // A bounce to the password wall means the post was swallowed, not
          // processed — don't show a false "sent" confirmation.
          if (response.url && response.url.indexOf('/password') !== -1) {
            throw new Error('contact post hit the password wall');
          }
          if (!response.ok) throw new Error('contact post failed: ' + response.status);
          this.showReportSuccess();
        }.bind(this))
        .catch(function () {
          if (errorEl) {
            errorEl.textContent = errorEl.getAttribute('data-generic-error') || '';
            errorEl.hidden = false;
          }
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
    }

    showReportSuccess() {
      var modal = document.getElementById('ReportModal');
      if (!modal) return;
      var wrap = modal.querySelector('[data-ref="report-form-wrap"]');
      var success = modal.querySelector('[data-ref="report-success"]');
      var bodyField = modal.querySelector('#ReportContact-body');
      if (bodyField) bodyField.value = '';
      if (wrap) wrap.hidden = true;
      if (success) success.hidden = false;
    }

    // ---- Feedback / reporting ---------------------------------------------
    // Open the report modal in one of two modes: "generation_issue" (carries the
    // current design context) or "missing_course" (just the typed course name).
    openReport(kind) {
      var modal = document.getElementById('ReportModal');
      if (!modal || typeof modal.show !== 'function') return;
      var set = function (ref, val) {
        var el = modal.querySelector('[data-ref="' + ref + '"]');
        if (el) el.value = val == null ? '' : String(val);
      };
      set('report-type', kind === 'missing_course' ? 'Missing course request' : 'Design issue');
      set('report-course', (this.searchEl && this.searchEl.value.trim()) || '');
      set('report-course-id', this.selectedCourseId != null ? this.selectedCourseId : '');
      set('report-token', kind === 'generation_issue' ? (this.designToken || '') : '');
      set('report-page', window.location.href);

      var intro = modal.querySelector('[data-ref="report-intro"]');
      if (intro) intro.textContent = this.reportIntro[kind] || '';
      var heading = modal.querySelector('[data-ref="report-heading"]');
      if (heading && this.reportLabel[kind]) heading.textContent = this.reportLabel[kind];

      // Reset to the form view (a previous submit may have shown the success panel).
      var wrap = modal.querySelector('[data-ref="report-form-wrap"]');
      var success = modal.querySelector('[data-ref="report-success"]');
      var error = modal.querySelector('[data-ref="report-error"]');
      if (wrap) wrap.hidden = false;
      if (success) success.hidden = true;
      if (error) error.hidden = true;

      modal.show(kind === 'missing_course' ? this.missingCourseBtn : this.reportIssueBtn);
    }

    // ---- Arriving from the homepage ---------------------------------------
    //
    // The hero search (assets/hero-course-search.js) captures which course the
    // customer means but generates nothing — it hands the choice over in the
    // URL and lets whichever garment they picked do the rendering. Take it,
    // put the picker in the state the hero promised, and design it: they have
    // already chosen a course and a garment, so the next thing they should see
    // is the two together, not a filled-in form waiting on a button.

    // Numeric ids arrive as strings from the query string; the API is given
    // them as the numbers the autocomplete would have supplied.
    incomingId(value) {
      if (value == null || value === '') return null;
      return /^\d+$/.test(value) ? parseInt(value, 10) : value;
    }

    // Returns true when the URL carried a course and this took it — the caller
    // uses that to decide whether a stored design should be restored instead.
    applyIncomingCourse() {
      if (!this.searchEl) return false;

      var params;
      try { params = new URLSearchParams(window.location.search); }
      catch (_) { return false; }

      var name = (params.get('course') || '').trim();
      if (!name) return false;

      var routing = params.get('routing');

      // The address bar carries the course on screen (syncCourseUrl), so a
      // reload — or a link back to this page from the same visit — arrives here
      // with the design already in storage. Repaint that, title and all, rather
      // than render the course again over the customer's edits.
      if (this.isSavedCourse(readSavedDesign(), params)) return false;

      this.selectedCourseId = this.incomingId(params.get('course_id'));
      this.selectedOsmId = this.incomingId(params.get('osm_id'));

      this.searchEl.value = name;
      this.propCourse.value = name;
      this.propCourseId.value =
        this.selectedCourseId != null ? String(this.selectedCourseId) : '';
      if (this.generateEl) this.generateEl.disabled = false;

      // Rebuild the search result the hero picked, in the shape selectSuggestion
      // would have left behind. Everything downstream of a selection reads this
      // — the title suggestions and the recent-courses list both — so without it
      // a customer arriving through the hero got a shorter row of chips (no
      // club, no location) than the same course picked in the field below, and
      // the course they designed was never remembered. `club`/`place` are the
      // hero's display-only qualifiers; the course itself is resolved from the
      // ids, exactly as before. A `routing` in the URL only exists at a club
      // with more than one course, which is what n_routings has to say for a
      // recent chip to re-open the picker rather than silently take the first.
      this._selectedItem = {
        name: name,
        id: this.selectedCourseId,
        osm_id: this.selectedOsmId,
        operator: (params.get('club') || '').trim(),
        place: (params.get('place') || '').trim(),
        n_routings: routing ? 2 : 0,
      };

      // The parameters stay in the address bar: syncCourseUrl rewrites them
      // whenever the customer designs a different course, so the homepage's
      // choice is never pushed back over their own, and the address is always
      // a link to what is on screen.

      // At a multi-course club the hero committed to the whole-club map rather
      // than asking before the customer had seen anything. Load the picker so
      // the club's own courses are visible and one click away, select what was
      // promised, and only then generate — generating first would render the
      // club's first course instead, and the picker would follow that.
      if (routing && this.selectedOsmId != null) {
        var pending = this.loadRoutings(this.selectedOsmId);
        if (pending && pending.then) {
          pending
            .then(function () {
              this.markRoutingSelected(routing);
              this.onGenerate();
            }.bind(this))
            .catch(function () { this.onGenerate(); }.bind(this));
          return true;
        }
      }

      this.onGenerate();
      return true;
    }

    // Whether a stored design is the course a URL names. Ids decide when both
    // sides have one; the name only when neither does. Routing must match too:
    // a link to one course at a multi-course club is not the whole-club map.
    isSavedCourse(saved, params) {
      if (!saved) return false;
      var str = function (v) { return v == null ? '' : String(v); };
      if (str(saved.routing) !== str(params.get('routing'))) return false;
      var osm = str(params.get('osm_id'));
      if (osm && str(saved.osmId)) return osm === str(saved.osmId);
      var id = str(params.get('course_id'));
      if (id && str(saved.courseId)) return id === str(saved.courseId);
      var name = (params.get('course') || '').trim();
      return !!name && name === (saved.search || saved.course || '').trim();
    }

    // ---- The address bar as a link to the design ---------------------------
    //
    // Whatever course is on screen is in the URL, in the parameters
    // applyIncomingCourse reads, so copying the address bar shares it just as
    // the Share button does. Only the course travels; the title and colours
    // are the recipient's to choose. Other parameters (the variant) are kept.

    courseLinkParams(base) {
      var params = new URLSearchParams(base || '');
      var fields = this.courseLinkFields();
      Object.keys(fields).forEach(function (key) {
        params.delete(key);
        if (fields[key] != null && fields[key] !== '') params.set(key, String(fields[key]));
      });
      return params;
    }

    syncCourseUrl() {
      if (!window.history || !window.history.replaceState) return;
      if (!this.searchEl || !this.searchEl.value.trim()) return;
      var qs;
      try { qs = this.courseLinkParams(window.location.search).toString(); }
      catch (_) { return; }
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + (qs ? '?' + qs : '') + window.location.hash
      );
    }

    // ---- Carrying a design between products -------------------------------
    //
    // See STORE_KEY above for why this exists. saveDesign() runs at every point
    // the design or its title changes; restoreSavedDesign() runs once, on a page
    // that has no course in its URL.

    // Hand the current design to whatever product page the customer opens next.
    // Only the hosted map URLs travel — the inline-PNG fallback the API serves
    // in local development is megabytes of base64 and would blow the storage
    // quota, so that design is carried by identity alone and re-rendered on
    // arrival (see restoreSavedDesign).
    saveDesign() {
      if (this._hasDesign !== true) return;
      var variants = {};
      var hosted = false;
      Object.keys(this.variants || {}).forEach(function (ink) {
        var v = this.variants[ink];
        if (!v || !v.url) return;
        variants[ink] = { url: v.url, title_ink: v.title_ink };
        hosted = true;
      }.bind(this));

      writeSavedDesign({
        v: 1,
        savedAt: Date.now(),
        // What the customer typed and what the API resolved it to: the first
        // goes back in the search box, the second on the order.
        search: this.searchEl ? this.searchEl.value : '',
        course: this.propCourse ? this.propCourse.value : '',
        courseId: this.selectedCourseId,
        osmId: this.selectedOsmId,
        routing: this.selectedRouting,
        routingName: this.propRoutingName ? this.propRoutingName.value : '',
        title: this.titleEl ? this.titleEl.value : '',
        // What the title chips are built from (see renderTitleChips): the
        // resolved name plus the club and town of the course picked.
        defaultTitle: this._defaultTitle || '',
        item: this._selectedItem ? {
          name: courseNameOf(this._selectedItem),
          id: this._selectedItem.id != null ? this._selectedItem.id : null,
          osm_id: this._selectedItem.osm_id != null ? this._selectedItem.osm_id : null,
          n_routings: this._selectedItem.n_routings || 0,
          operator: this._selectedItem.operator || '',
          place: coursePlaceOf(this._selectedItem) || '',
        } : null,
        // Layout is colour-independent, so one title box serves both inks.
        titleCtx: hosted ? this.titleCtx : null,
        variants: variants,
        completeness: this.completeness,
        // Kept for the recolour/proof fast paths. It may well have expired by
        // the time it is read; every use of it falls back (see recolor and
        // bakeProofForGarment), so a dead token costs nothing.
        token: this.designToken || null,
      });
    }

    // Repaint a design made on another garment onto this one. Everything the
    // designer holds after a generate is restored — course, picker, title,
    // completeness notice, line-item properties — so the customer lands on a
    // page they can keep editing, not a picture of their design.
    restoreSavedDesign() {
      if (!this.searchEl || !this.titleEl) return;
      // Nowhere to paint a design (a theme without the overlay) — leave the
      // placeholder rather than enable Add to Cart against an invisible design.
      if (!this.mapEl && !this.overviewMapEl) return;
      var saved = readSavedDesign();
      if (!saved) return;
      var name = (saved.search || saved.course || '').trim();
      if (name.length < 2) return;

      this.searchEl.value = name;
      if (this.propCourse) this.propCourse.value = saved.course || name;
      this.selectedCourseId = saved.courseId == null ? null : saved.courseId;
      this.selectedOsmId = saved.osmId == null ? null : saved.osmId;
      this.selectedRouting = saved.routing == null ? null : saved.routing;
      if (this.propCourseId) {
        this.propCourseId.value = this.selectedCourseId != null ? String(this.selectedCourseId) : '';
      }
      if (this.propRouting) {
        this.propRouting.value = this.selectedRouting != null ? String(this.selectedRouting) : '';
      }
      if (this.propRoutingName) this.propRoutingName.value = saved.routingName || '';
      this._selectedItem = saved.item || null;
      this._defaultTitle = saved.defaultTitle || '';
      if (this.generateEl) this.generateEl.disabled = false;
      // The design on screen is this one, so re-picking the same course in the
      // picker below stays the no-op it is after a generate.
      this._designedKey = this.designKey();
      this.restoreRoutingPicker(saved);

      var stored = saved.variants && saved.variants[this.courseInk];
      if (!saved.titleCtx || !stored || !stored.url) {
        // Nothing paintable travelled. Render it again for this garment instead,
        // keeping the title the customer wrote.
        this._pendingTitle = saved.title || '';
        this.onGenerate();
        return;
      }

      this.designToken = saved.token || null;
      this.storeVariants(Object.keys(saved.variants).map(function (ink) {
        return {
          color: ink,
          url: saved.variants[ink].url,
          title_ink: saved.variants[ink].title_ink,
        };
      }));
      var variant = this.variants[this.courseInk];
      // applyMap takes a course-overview response, so hand it the stored design
      // in that shape: same map, same box, this garment's ink.
      this.applyMap({
        url: variant.url,
        title_box: saved.titleCtx.box,
        title_ink: variant.title_ink || saved.titleCtx.ink,
        canvas_w_mm: saved.titleCtx.canvasW,
        canvas_h_mm: saved.titleCtx.canvasH,
        color: this.courseInk,
      });
      this.applyCompleteness(saved.completeness);
      // The satellite view is served against the render token, so it only comes
      // back while that is still live; otherwise the panel keeps its example,
      // as it does whenever a satellite cut is unavailable.
      this.loadSatellite(this.designToken);

      // The title comes back as the customer left it — including their own line
      // breaks — and stays live and editable, which is the whole point of
      // carrying the design rather than the flattened proof.
      if (this.titleFieldEl) this.titleFieldEl.classList.add('is-revealed');
      this.titleEl.disabled = false;
      this.titleEl.value = saved.title || '';
      this.growTitleField();
      if (this.propTitle) this.propTitle.value = this.titleEl.value;
      this.renderTitleChips(this._defaultTitle);
      if (this.reportIssueBtn) this.reportIssueBtn.hidden = false;
      // This garment has no proof yet; it is baked at add-to-cart (onSubmit).
      this.setProofProp('');
      this.gateSubmit(true);
      this.syncCourseUrl();
      ensureTitleFont().then(function () { this.renderTitle(); }.bind(this));
    }

    // Put the multi-course picker back up for a carried design, so the customer
    // can still switch course on the new garment. Only for a club that had one:
    // loading it clears the routing properties first, and an ordinary
    // single-course club would be left with them emptied for nothing.
    restoreRoutingPicker(saved) {
      if (saved.routing == null || saved.osmId == null) return;
      var routing = saved.routing;
      var name = saved.routingName || '';
      var pending = this.loadRoutings(saved.osmId);
      // loadRoutings clears the picker (and the properties with it) before it
      // fetches — put them straight back so an add-to-cart during the round-trip
      // still carries which course this is.
      this.selectedRouting = routing;
      if (this.propRouting) this.propRouting.value = String(routing);
      if (this.propRoutingName) this.propRoutingName.value = name;
      if (!pending || !pending.then) return;
      pending.then(function () {
        if (this._routings.length) {
          this.markRoutingSelected(routing); // silent: re-selects, never regenerates
          return;
        }
        // The list didn't come back — restore what clearRoutings wiped so the
        // order still carries which course this is.
        this.selectedRouting = routing;
        if (this.propRouting) this.propRouting.value = String(routing);
        if (this.propRoutingName) this.propRoutingName.value = name;
      }.bind(this));
    }

    // ---- Autocomplete -----------------------------------------------------
    onSearchInput() {
      this.selectedCourseId = null;
      this.selectedOsmId = null;
      this._selectedItem = null;
      // Editing the club name invalidates the course choice with it — a picker
      // left showing Cherington while the box says a different club is how the
      // wrong course ends up on an order.
      this.clearRoutings();
      clearTimeout(this._acDebounce);
      // Typing is the answer to whatever the nearby line last said, so the line
      // goes and the panel is free to close on its own again.
      this._nearbyPinned = false;
      this.setNearbyNote('');
      var q = this.searchEl.value.trim();
      if (this.generateEl) this.generateEl.disabled = q.length < 2;
      if (q.length < 2) {
        if (this._acAbort) { this._acAbort.abort(); this._acAbort = null; }
        this.suggestionsEl.hidden = true;
        this.showNoResults(false);
        this.showEntry();
        return;
      }
      // 150ms debounce + AbortController so a slow response for an older
      // keystroke can't clobber the dropdown after the user has typed further.
      this._acDebounce = setTimeout(function () {
        if (this._acAbort) this._acAbort.abort();
        this._acAbort = new AbortController();
        var signal = this._acAbort.signal;
        fetch(this.apiBase + '/api/search?q=' + encodeURIComponent(q), { signal })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (!signal.aborted) this.showSuggestions(data.results || []);
          }.bind(this))
          .catch(function (err) {
            if (err.name !== 'AbortError') this.suggestionsEl.hidden = true;
          }.bind(this));
      }.bind(this), 150);
    }

    // ---- Before anything is typed ------------------------------------------
    // Deliberately not a list of famous courses. The store's own search data
    // says customers look up their own club rather than the Open venues, so a
    // "popular courses" panel would answer a question nobody here is asking.
    // These two narrow towards the customer instead: where they are, and what
    // they have designed before.

    onSearchFocus() {
      this.probeNearby();
      if (!this.searchEl.value.trim()) this.showEntry();
    }

    showEntry() {
      if (!this.entryEl) return;
      // Nothing to offer is not an empty panel — it is no panel.
      var hasNearby = this.nearbyEl && !this.nearbyEl.hidden;
      var hasRecent = this.recentEl && !this.recentEl.hidden;
      this.entryEl.hidden = !(hasNearby || hasRecent);
    }

    hideEntry() {
      // Pinned while a nearby run is in flight or has a line to show: the panel
      // is where that answer lands, and closing it would throw the answer away
      // at the moment the customer went looking for it.
      if (this._nearbyPinned) return;
      if (this.entryEl) this.entryEl.hidden = true;
    }

    showNoResults(show) {
      if (this.noResultsEl) this.noResultsEl.hidden = !show;
    }

    // ---- Courses this browser has designed before --------------------------

    readRecent() {
      try {
        var raw = window.localStorage.getItem(RECENT_KEY);
        var list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
      } catch (e) {
        // Private mode, storage disabled, or something else's data under our
        // key. None of it is worth a broken search field.
        return [];
      }
    }

    // Stored in the shape /api/search returns, so a recent course replays
    // through selectSuggestion with no second code path — routing count
    // included, or a multi-course club picked from here would silently skip
    // the question of which course.
    rememberCourse(item) {
      if (!item) return;
      var name = courseNameOf(item);
      if (!name) return;
      var entry = {
        name: name,
        id: item.id != null ? item.id : null,
        osm_id: item.osm_id != null ? item.osm_id : null,
        n_routings: item.n_routings || 0,
        operator: item.operator || '',
        place: coursePlaceOf(item) || '',
      };
      var list = this.readRecent().filter(function (c) {
        return c && c.name !== name;
      });
      list.unshift(entry);
      try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
      } catch (e) { /* nothing here is worth failing a generate over */ }
      this.renderRecent();
    }

    renderRecent() {
      if (!this.recentEl || !this.recentChipsEl) return;
      var list = this.readRecent();
      this.recentChipsEl.innerHTML = '';
      if (!list.length) {
        this.recentEl.hidden = true;
        return;
      }
      list.forEach(function (course) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'course-designer__chip';
        chip.textContent = course.name;
        // mousedown, like the suggestions: it has to beat the field's blur.
        chip.addEventListener('mousedown', function (e) {
          e.preventDefault();
          this._acItems = [course];
          this.selectSuggestion(0);
          this.hideEntry();
        }.bind(this));
        this.recentChipsEl.appendChild(chip);
      }.bind(this));
      this.recentEl.hidden = false;
    }

    // ---- Courses near the customer -----------------------------------------
    //
    // One tap instead of spelling a club name on a phone keyboard, which is the
    // thing most of this store's customers are actually trying to do. The three
    // ways it can end without a list are kept apart all the way through: what
    // the customer has to do next is different in each, and a single "couldn't
    // get your location" was wrong in two of them.

    // The endpoint is newer than this theme and may not be deployed yet, so the
    // button appears only once the API has answered for it. Probed on first
    // focus rather than on load — one request, at a moment where its latency
    // costs nothing — and remembered for the session.
    probeNearby() {
      if (!this.nearbyEl || !this.apiBase || this._nearbyProbed) return;
      this._nearbyProbed = true;
      if (!navigator.geolocation) return;
      var cached = null;
      try { cached = window.sessionStorage.getItem(NEARBY_KEY); } catch (e) {}
      if (cached === 'no') return;
      if (cached === 'yes') {
        this.nearbyEl.hidden = false;
        this.showEntry();
        return;
      }
      fetch(this.apiBase + '/api/nearby?lat=0&lon=0&limit=1')
        .then(function (r) {
          try { window.sessionStorage.setItem(NEARBY_KEY, r.ok ? 'yes' : 'no'); } catch (e) {}
          if (!r.ok) return;
          this.nearbyEl.hidden = false;
          this.showEntry();
        }.bind(this))
        .catch(function () { /* offline, or no API: the field still works */ });
    }

    // The button is its own progress indicator: the label says what is
    // happening and the reticle pulses, right under the finger that started it.
    // The status line below Generate — where this used to report — is a screen
    // away on a phone, so the customer tapped and nothing appeared to happen.
    setNearbyBusy(busy) {
      if (!this.nearbyEl) return;
      this.nearbyEl.classList.toggle('is-locating', busy);
      this.nearbyEl.disabled = busy;
      this.nearbyEl.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (this.nearbyLabelEl) {
        if (busy) this._nearbyIdleLabel = this.nearbyLabelEl.textContent;
        this.nearbyLabelEl.textContent = busy
          ? NEARBY_LOCATING
          : (this._nearbyIdleLabel || this.nearbyLabelEl.textContent);
      }
    }

    // Why there is no list. Sits in the entry panel under the button rather
    // than in the designer's status line, so the answer is where the question
    // was asked. Live region, because by now the customer may have looked away.
    setNearbyNote(text) {
      if (!this.nearbyNoteEl) return;
      this.nearbyNoteEl.textContent = text || '';
      this.nearbyNoteEl.hidden = !text;
      if (text) {
        this._nearbyPinned = true;
        if (this.entryEl) this.entryEl.hidden = false;
      }
    }

    onNearbyClick() {
      if (!navigator.geolocation) return;
      // Held open through the whole run: the permission prompt blurs the field,
      // and the panel this button lives in closes 200ms after a blur.
      this._nearbyPinned = true;
      this.setNearbyNote('');
      this.setNearbyBusy(true);
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          this.fetchNearby(pos.coords.latitude, pos.coords.longitude);
        }.bind(this),
        function (err) {
          this.setNearbyBusy(false);
          // PERMISSION_DENIED is the only one the customer can act on directly,
          // and it is the only one where "try again" would just be refused
          // again — so it gets the instruction and the others get the retry.
          this.setNearbyNote(
            err && err.code === 1 ? NEARBY_DENIED : NEARBY_NO_FIX
          );
        }.bind(this),
        { timeout: 8000, maximumAge: 600000 }
      );
    }

    // One request for one rung of the ladder. Resolves to the rows or to null
    // for anything that went wrong, so both callers can say their own thing
    // about it — a first search that fails is a failure, and a widening that
    // fails just leaves the list the customer already has.
    requestNearby(lat, lon, step) {
      var url = this.apiBase + '/api/nearby?lat=' + encodeURIComponent(lat) +
        '&lon=' + encodeURIComponent(lon) +
        '&radius_km=' + step.radius_km +
        '&limit=' + step.limit;
      return fetch(url)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) { return data ? (data.results || []) : null; })
        .catch(function () { return null; });
    }

    fetchNearby(lat, lon) {
      this._nearbyOrigin = { lat: lat, lon: lon };
      this._nearbyStep = 0;
      this.requestNearby(lat, lon, NEARBY_STEPS[0])
        .then(function (results) {
          this.setNearbyBusy(false);
          if (results === null) {
            this.setNearbyNote(NEARBY_NO_FIX);
            return;
          }
          if (!results.length) {
            // The location worked; there is simply no golf inside the radius.
            // Saying which radius is what stops this reading as a failure, and
            // the pull that would widen it needs a list to be pulled past —
            // with nothing to show there is nowhere to put it, so this offers
            // the wider search as the sentence instead.
            this.setNearbyNote(
              'No courses within ' + radiusLabel() + '. Try searching by name.'
            );
            return;
          }
          this._nearbyPinned = false;
          this.showSuggestions(results, this._nearbyOrigin);
        }.bind(this));
    }

    // Pull past the end of the list: the next rung of the ladder, rendered in
    // place. The rows are a superset of the ones already on screen and in the
    // same order, so the scroll position is kept — the customer stays where
    // they were and the new courses arrive underneath them.
    expandNearby(done) {
      var step = NEARBY_STEPS[this._nearbyStep + 1];
      var origin = this._nearbyOrigin;
      if (!step || !origin) { done(true); return; }
      var had = this._acItems.length;
      var scrollTop = this.suggestionsEl.scrollTop;
      this.requestNearby(origin.lat, origin.lon, step)
        .then(function (results) {
          // A failed widening is not worth a message: the list the customer
          // already has is still on screen and still the answer.
          if (results === null) { done(false); return; }
          this._nearbyStep += 1;
          var foundMore = results.length > had;
          if (foundMore) {
            this.showSuggestions(results, origin);
            this.suggestionsEl.scrollTop = scrollTop;
          }
          // Two ways this is the end, and both are worth saying rather than
          // hiding: the ladder has no rung left, or a wider search brought back
          // nothing the customer couldn't already see. The second is why the
          // ladder isn't just counted — a country with three courses in it
          // reaches the end at 250km and shouldn't be offered 500.
          done(!foundMore || !NEARBY_STEPS[this._nearbyStep + 1]);
        }.bind(this));
    }

    // `origin` is set only for a nearby run: the coordinate the list was
    // measured from, which turns each row's coordinates into the distance that
    // explains the order. A typed search has no origin and no distances.
    showSuggestions(items, origin) {
      this.showNoResults(false);
      this.setNearbyNote('');
      this.hideEntry();
      this._acItems = items;
      this._acActive = -1;
      this.suggestionsEl.innerHTML = '';
      if (!items.length) {
        this.suggestionsEl.hidden = true;
        this.showNoResults(true);
        return;
      }
      if (origin) {
        // Not an option — a caption on the set below it. role="presentation"
        // keeps it out of the listbox, and every query for a row names the
        // suggestion class rather than `li` so the keyboard never lands here.
        var head = document.createElement('li');
        head.className = 'course-designer__suggestions-head';
        head.setAttribute('role', 'presentation');
        head.textContent = NEARBY_HEADING;
        this.suggestionsEl.appendChild(head);
      }
      items.forEach(function (item, idx) {
        var li = document.createElement('li');
        li.className = 'course-designer__suggestion';
        li.setAttribute('role', 'option');
        // Two lines: the name, then one quiet line carrying both qualifiers.
        // Course names repeat worldwide (there's a Sunningdale in Berkshire
        // and another in Ontario) and often aren't recognisable alone — OSM
        // names Royal Portrush's two courses "Dunluce Links" and "Valley
        // Links" — so the customer needs the club and the place to pick the
        // right one. Both display only; the ordered value is still the name.
        var nameEl = document.createElement('span');
        nameEl.className = 'course-designer__suggestion-name';
        nameEl.textContent = courseNameOf(item);
        li.appendChild(nameEl);
        var place = coursePlaceOf(item);
        // How far, on the same quiet line and flush to its right edge — the
        // sort order, made readable. Down a column of tabular figures it is the
        // one thing that says why these courses and why in this order.
        var coords = origin ? coordsOf(item) : null;
        var distance = coords
          ? formatDistance(distanceKm(origin.lat, origin.lon, coords.lat, coords.lon))
          : '';
        if (item.operator || place || distance) {
          var metaEl = document.createElement('span');
          metaEl.className = 'course-designer__suggestion-meta';
          // The club and the place share one span so the distance can be the
          // meta line's other end rather than a third thing in a row of three.
          var qualifiersEl = document.createElement('span');
          qualifiersEl.className = 'course-designer__suggestion-qualifiers';
          if (item.operator) {
            var clubEl = document.createElement('span');
            clubEl.className = 'course-designer__suggestion-club';
            clubEl.textContent = item.operator;
            qualifiersEl.appendChild(clubEl);
          }
          if (place) {
            var placeEl = document.createElement('span');
            placeEl.className = 'course-designer__suggestion-place';
            placeEl.textContent = place;
            qualifiersEl.appendChild(placeEl);
          }
          metaEl.appendChild(qualifiersEl);
          if (distance) {
            var distEl = document.createElement('span');
            distEl.className = 'course-designer__suggestion-distance';
            distEl.textContent = distance;
            metaEl.appendChild(distEl);
          }
          li.appendChild(metaEl);
        }
        // mousedown (not click) so it fires before the input's blur handler.
        li.addEventListener('mousedown', function (e) {
          e.preventDefault();
          this.selectSuggestion(idx);
        }.bind(this));
        this.suggestionsEl.appendChild(li);
      }.bind(this));
      // Last child, and only over a nearby list: pulling past the end of it is
      // how the radius widens. The list is rebuilt from scratch on every render,
      // so this is re-mounted every time rather than surviving one.
      var more = this.nearbyMore();
      if (more) {
        more.setEnabled(!!origin);
        if (origin) more.mount(this.suggestionsEl);
      }
      this.suggestionsEl.hidden = false;
    }

    // Built on first use, and only where the shared controller was loaded: a
    // theme that hasn't added course-nearby-more.js still gets a working
    // nearby list, it just opens at one radius.
    nearbyMore() {
      if (this._nearbyMore !== undefined) return this._nearbyMore;
      this._nearbyMore = window.CourseNearbyMore
        ? new window.CourseNearbyMore({
          scroller: this.suggestionsEl,
          prefix: 'course-designer',
          labels: NEARBY_MORE_LABELS,
          onExpand: this.expandNearby.bind(this),
        })
        : null;
      return this._nearbyMore;
    }

    selectSuggestion(idx) {
      var item = this._acItems[idx];
      if (!item) return;
      // The name alone, never `label` — that has "(City, Country)" glued on
      // for the dropdown, and this value ends up on the customer's order.
      var name = courseNameOf(item);
      this._selectedItem = item;
      this.searchEl.value = name;
      this.selectedCourseId = item.id != null ? item.id : null;
      this.selectedOsmId = item.osm_id != null ? item.osm_id : null;
      this.propCourse.value = name;
      this.propCourseId.value = this.selectedCourseId != null ? String(this.selectedCourseId) : '';
      this.suggestionsEl.hidden = true;
      this.showNoResults(false);
      this.hideEntry();
      this._acItems = [];
      if (this.generateEl) this.generateEl.disabled = false;
      // Picking the course is the design decision, so it generates straight
      // away — there is no button to press on the product page. The exception
      // is a club with more than one course (`n_routings` rides along on the
      // search result so only those clubs fetch): there the question is still
      // open, so the picker comes up blank and its click generates (see
      // selectRouting). If the picker can't load, generate anyway — the API
      // falls back to the club's first course.
      if (item.n_routings > 1) {
        var routings = this.loadRoutings(item.osm_id);
        var fallback = function () {
          if (this._selectedItem === item && !this._routings.length) this.onGenerate();
        }.bind(this);
        if (routings) routings.then(fallback);
        else fallback();
      } else {
        this.onGenerate();
      }
      // Deliberately NO /api/prewarm here. A prewarm is a full match + render
      // (4-40s of CPU) on a single-process API, and on this page selecting a
      // suggestion is the generate — so the warm never finishes first, and the customer's own /api/course-overview ends
      // up queued behind their own speculative render of the same course. A
      // real customer browsed four courses before choosing one and waited 107s
      // for a map that was only slow because of the three they didn't pick.
      //
      // Warming still pays where there is genuine dead time to hide it in: the
      // hero fires it as the customer clicks through to a product page, which
      // is ~2s of navigation they were spending anyway (hero-course-search.js).
    }

    onSearchKeydown(e) {
      // Enter with no suggestion highlighted generates whatever is typed — the
      // way to design a club the autocomplete didn't offer, now that there is
      // no button to press.
      if (e.key === 'Enter' && (this.suggestionsEl.hidden || this._acActive < 0)) {
        e.preventDefault();
        this.suggestionsEl.hidden = true;
        this.onGenerate();
        return;
      }
      if (this.suggestionsEl.hidden) return;
      // The rows, not every <li>: a nearby list is captioned by one that isn't
      // an option, and counting it would put the arrow keys an index out.
      var lis = this.suggestionsEl.querySelectorAll('.course-designer__suggestion');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.setActive(Math.min(this._acActive + 1, lis.length - 1), lis);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.setActive(Math.max(this._acActive - 1, 0), lis);
      } else if (e.key === 'Enter' && this._acActive >= 0) {
        e.preventDefault();
        this.selectSuggestion(this._acActive);
      } else if (e.key === 'Escape') {
        this.suggestionsEl.hidden = true;
      }
    }

    setActive(idx, lis) {
      lis.forEach(function (li) { li.classList.remove('is-active'); });
      if (idx >= 0 && idx < lis.length) {
        lis[idx].classList.add('is-active');
        lis[idx].scrollIntoView({ block: 'nearest' });
        this._acActive = idx;
      }
    }

    // ---- Multi-course clubs -----------------------------------------------
    //
    // Some clubs pack two or three courses into one OSM boundary — Minchinhampton
    // has the Avening and Cherington, Celtic Manor three — and both courses tag
    // their holes 1..18. Until the customer says which one they played there is
    // no single right map to draw, so the picker appears before Generate rather
    // than after. Ordinary single-course clubs never see it.

    clearRoutings() {
      this._routings = [];
      this.selectedRouting = null;
      if (this.routingEl) this.routingEl.innerHTML = '';
      if (this.routingFieldEl) this.routingFieldEl.hidden = true;
      if (this.routingNoteEl) this.routingNoteEl.hidden = true;
      this.setRoutingProps(null);
    }

    // Mirror the choice onto the order. The name is customer-facing because at
    // Minchinhampton "Avening" versus "Cherington" *is* the order; the index is
    // hidden and exists so fulfilment can reproduce the exact render.
    setRoutingProps(item) {
      if (this.propRouting) {
        this.propRouting.value = item ? String(item.index) : '';
      }
      if (this.propRoutingName) {
        this.propRoutingName.value = item ? item.name : '';
      }
    }

    loadRoutings(osmId) {
      this.clearRoutings();
      if (!this.routingEl || !this.routingFieldEl) return;   // snippet not updated
      if (osmId == null) return;

      return fetch(this.apiBase + '/api/course-routings?osm_id=' + encodeURIComponent(osmId))
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var list = (data && data.routings) || [];
          if (list.length < 2) return;
          // Only the club's own courses are still selected by index; the
          // whole-club entry is relabelled from theme settings so the wording
          // stays editable without touching this file.
          this._routings = list.map(function (opt) {
            return opt.index === 'all'
              ? Object.assign({}, opt, { name: this.routingAllLabel })
              : opt;
          }.bind(this));
          this.renderRoutings();
          // Deliberately no preselect: which course this is IS the decision, so
          // nothing is highlighted until the customer picks one. selectRouting
          // (fromUser) then generates immediately off that click. Generate stays
          // enabled in the meantime — the API defaults an un-picked routing to
          // the club's first course, and syncRoutingFromResponse reconciles the
          // picker to whichever one actually rendered.
        }.bind(this))
        .catch(function () {
          // Best-effort. With no picker the API still renders the club's first
          // course rather than the old two-courses-on-one-map result, so a
          // failure here degrades to a sensible design, not a broken one.
        });
    }

    renderRoutings() {
      this.routingEl.innerHTML = '';
      this._routings.forEach(function (opt, idx) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'course-designer__routing-option';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.dataset.idx = String(idx);

        var name = document.createElement('span');
        name.className = 'course-designer__routing-name';
        name.textContent = opt.name;
        btn.appendChild(name);

        var meta = document.createElement('span');
        meta.className = 'course-designer__routing-meta';
        meta.textContent = opt.par_total
          ? opt.holes + ' holes · par ' + opt.par_total
          : opt.holes + ' holes';
        btn.appendChild(meta);

        btn.addEventListener('click', function () { this.selectRouting(idx, true); }.bind(this));
        this.routingEl.appendChild(btn);
      }.bind(this));
      this.routingFieldEl.hidden = false;
    }

    // `fromUser` is true only for a click on an option. Picking the course at a
    // multi-course club IS the design decision — the picker only appears once the
    // club is already chosen — so the click generates rather than leaving them to
    // find Design It. Programmatic calls (the preselect, and the retrospective
    // sync from a response) must stay silent, or the sync would loop the generate
    // it came from.
    selectRouting(idx, fromUser) {
      var opt = this._routings[idx];
      if (!opt) return;
      this.selectedRouting = opt.index;
      this.setRoutingProps(opt);

      var buttons = this.routingEl.querySelectorAll('.course-designer__routing-option');
      Array.prototype.forEach.call(buttons, function (b, i) {
        var on = i === idx;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      // The whole-club map has no scorecard on it. Say so here, before they
      // generate, rather than letting them notice it missing afterwards.
      if (this.routingNoteEl) {
        var note = opt.scorecard === false ? this.routingAllNote : '';
        this.routingNoteEl.textContent = note;
        this.routingNoteEl.hidden = !note;
      }

      // Re-picking the course the map on screen was already drawn from would buy
      // nothing, so that one click is a no-op; anything else renders.
      if (!fromUser) return;
      if (this._generating && this.designKey() === this._designedKey) return;
      if (this._hasDesign === true && this.designKey() === this._designedKey) return;
      this.onGenerate();
    }

    // What the design on screen was rendered from. The routing index alone won't
    // do: switching from one multi-course club to another lands back on index 0,
    // which would read as "already drawn" and swallow the generate.
    designKey() {
      return [this.selectedCourseId, this.selectedOsmId, this.selectedRouting].join('|');
    }

    // A customer can type a club name and hit Design It without ever touching
    // the autocomplete, so the picker never loaded and they were never asked.
    // The API answers with its first course rather than refusing — but it says
    // which one in `routing`, so show the picker retrospectively and let them
    // switch. Without this they'd get the Avening silently and have no way of
    // knowing Cherington existed.
    syncRoutingFromResponse(co) {
      if (!co || co.routing === null || co.routing === undefined) return;
      if (this._routings.length) {
        // Picker already up: just make sure it agrees with what was rendered.
        this.markRoutingSelected(co.routing);
        return;
      }
      // The response carries the osm_id it resolved to, which is the only way
      // to know it when the customer never picked a suggestion.
      var osmId = co.osm_id != null ? co.osm_id : this.selectedOsmId;
      if (osmId == null) return;
      var rendered = co.routing;
      var p = this.loadRoutings(osmId);
      if (p) p.then(function () { this.markRoutingSelected(rendered); }.bind(this));
    }

    markRoutingSelected(index) {
      for (var i = 0; i < this._routings.length; i++) {
        if (String(this._routings[i].index) === String(index)) {
          this.selectRouting(i);
          return;
        }
      }
    }

    // ---- Generate ---------------------------------------------------------
    onGenerate() {
      var course = this.searchEl.value.trim();
      if (course.length < 2) return;
      this._generating = true;
      this._designedKey = this.designKey();
      if (this.generateEl) this.generateEl.disabled = true;
      this.setGenerateLoading(true);
      // Deliberately NOT cleared here: the map on screen is still the previous
      // design until this one lands, so its notice must stay with it. Success
      // swaps both together (onOverview); a failure leaves both alone.
      // Signal that generation has started so preview mirrors (e.g. the
      // coming-soon carousel) can show a loading state on the previews right
      // away — they can't infer this from design-changed, which fires only once
      // the finished map lands.
      this.emitGenerating(true);

      // Put a floor under the wait. Two timers, both cleared in `finally` so
      // neither outlives the request whatever it does:
      //   - SLOW_NOTICE_MS: say out loud that it's still going, because a
      //     spinner that has been turning for twenty seconds is indexed by the
      //     customer as broken, not as busy.
      //   - GENERATE_TIMEOUT_MS: abort, and hand them a retry.
      // `timedOut` distinguishes our own abort from a network failure — both
      // land in the same catch, and only one of them has a sensible retry.
      //
      // Generating on selection means a customer can pick a second course while
      // the first is still drawing. The newer pick wins: abort the old request
      // quietly, so its map can't land on top of the one they now want.
      if (this._generateAbort) {
        this._generateAbort.superseded = true;
        this._generateAbort.abort();
      }
      var controller = new AbortController();
      this._generateAbort = controller;
      var timedOut = false;
      var slowTimer = setTimeout(function () {
        this.setStatus(SLOW_NOTICE, '');
      }.bind(this), SLOW_NOTICE_MS);
      var timeoutTimer = setTimeout(function () {
        timedOut = true;
        controller.abort();
      }, GENERATE_TIMEOUT_MS);

      fetch(this.apiBase + '/api/course-overview', {
        method: 'POST',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course: course,
          course_id: this.selectedCourseId,
          osm_id: this.selectedOsmId,
          // Null at an ordinary club. At a multi-course one this is the whole
          // point — without it the API falls back to the club's first course.
          routing: this.selectedRouting,
          include_waterways: true,
          title_font: this.titleFont,
          color: this.courseInk,
        }),
      })
        .then(function (r) {
          return r.text().then(function (text) {
            var data;
            try { data = JSON.parse(text); }
            catch (_) { throw { message: text || ('Server error (HTTP ' + r.status + ')') }; }
            if (!r.ok) throw { data: data, status: r.status };
            return data;
          });
        })
        .then(this.onOverview.bind(this))
        .catch(function (err) {
          if (controller.superseded) return;
          if (timedOut) this.onTimeout();
          else this.onError(err);
        }.bind(this))
        .finally(function () {
          clearTimeout(slowTimer);
          clearTimeout(timeoutTimer);
          // A superseded request leaves the loading state to its replacement.
          if (this._generateAbort !== controller) return;
          this._generateAbort = null;
          this._generating = false;
          if (this.generateEl) this.generateEl.disabled = false;
          this.setGenerateLoading(false);
          this.emitGenerating(false);
        }.bind(this));
    }

    // Mirrors Dawn's own add-to-cart button pattern (product-form.js): a
    // `.loading` class fades the label and reveals the spinner absolutely
    // centred over it, so the button keeps its width instead of collapsing to
    // the spinner alone.
    setGenerateLoading(active) {
      if (!this.generateEl) return;   // product page: no button, the image ring says it
      this.generateEl.classList.toggle('loading', active);
      var spinner = this.generateEl.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !active);
    }

    // Broadcast generation start/stop so preview mirrors can show a loading
    // state for the whole round-trip. Success OR failure both end it (fired from
    // the fetch's finally), so a stuck loader can't outlive a failed generate.
    emitGenerating(active) {
      var on = active === true;
      // Also say it on the pictures themselves: `course-generating` on <html>
      // turns a ring over every product image (see
      // component-course-designer.css / component-course-dock.css). A root
      // class rather than a per-element one because the images that have to
      // react live in three separate subtrees — gallery slides, the overview
      // slide, the mobile dock — and the dock is re-rendered wholesale on a
      // colour swap, which would drop a class set on it directly.
      document.documentElement.classList.toggle('course-generating', on);
      document.dispatchEvent(new CustomEvent('course-designer:generating', {
        detail: { active: on },
      }));
    }

    // Resolve a (possibly relative) map URL against the API origin. Hosted
    // assets are absolute CDN URLs; the local-dev fallback returns "/api/files/…"
    // which is relative to the API, not the storefront.
    resolveUrl(url) {
      if (!url) return '';
      if (/^(https?:)?\/\//i.test(url) || url.indexOf('data:') === 0) return url;
      return this.apiBase + url;
    }

    // Cache every pre-rendered colour variant for instant swapping, preload the
    // images, and mirror each hosted URL into its line-item property.
    storeVariants(list) {
      this.variants = {};
      (list || []).forEach(function (v) {
        var url = this.resolveUrl(v.url);
        var src = url || (v.png_base64 ? 'data:image/png;base64,' + v.png_base64 : '');
        this.variants[v.color] = { src: src, url: url, title_ink: v.title_ink };
        if (url) { var img = new Image(); img.src = url; } // warm the browser cache
        var prop = this.propMap && this.propMap[v.color];
        if (prop) prop.value = url || '';
      }.bind(this));
    }

    // Apply a map image + title context from any course-overview/course-render
    // response. Shared by a fresh generate and an in-place recolour; it does NOT
    // touch the editable title text, so a recolour preserves the customer's edit.
    applyMap(data) {
      this.titleCtx = {
        box: data.title_box,
        ink: data.title_ink,
        canvasW: data.canvas_w_mm,
        canvasH: data.canvas_h_mm,
      };
      this.setGeomProp();
      this.currentMapSrc = data.png_base64
        ? 'data:image/png;base64,' + data.png_base64
        : this.resolveUrl(data.url);
      var viewBox = '0 0 ' + data.canvas_w_mm + ' ' + data.canvas_h_mm;
      this.mapImgs().forEach(function (img) { img.src = this.currentMapSrc; }.bind(this));
      // Generated maps have a white background; restore the CSS multiply blend so
      // it drops onto the garment (placeholder set this to 'normal').
      this.garmentMapImgs().forEach(function (img) { img.style.mixBlendMode = ''; });
      this.titleSvgs().forEach(function (svg) { svg.setAttribute('viewBox', viewBox); });
      if (this.overlayEl) this.overlayEl.hidden = false;
      this.revealOverview();
      if (data.color) {
        this.courseInk = data.color;
        if (this.propCourseInk) this.propCourseInk.value = data.color;
      }
      this.setOverviewBg(this.courseInk);
    }

    onOverview(co) {
      if (!this.overlayEl || !this.mapEl) {
        // Theme misconfiguration rather than an API error, but still nothing the
        // customer should read raw — route it through the generic modal.
        if (window.console && console.warn) console.warn('[course-designer] overlay element missing');
        this.showErrorModal();
        return;
      }
      this.designToken = co.token || null;
      this.storeVariants(co.variants);
      this.applyMap(co);
      this.loadSatellite(this.designToken);
      this.applyCompleteness(co.completeness);
      this.syncRoutingFromResponse(co);

      // Reveal + enable title editing. A new preview resets the title to the
      // freshly resolved course name (overwriting any previous edit) — except
      // for a design carried from another garment and re-rendered here, which
      // keeps the title the customer had written on it.
      if (this.titleFieldEl) this.titleFieldEl.classList.add('is-revealed');
      this.titleEl.disabled = false;
      var carriedTitle = this._pendingTitle;
      this._pendingTitle = null;
      // Kept so a carried design can rebuild the same suggestion chips.
      this._defaultTitle = co.default_title || '';
      this.titleEl.value = carriedTitle || co.default_title || '';
      // Only on a design that actually rendered: a course that failed is not
      // one to offer back on the next visit.
      this.rememberCourse(this._selectedItem);
      this.renderTitleChips(co.default_title);
      this.growTitleField();
      this.propTitle.value = this.titleEl.value;
      if (!this.propCourse.value) this.propCourse.value = co.default_title || '';

      this.setStatus('', '');
      // A design now exists — reveal the contextual "something not right?" link.
      if (this.reportIssueBtn) this.reportIssueBtn.hidden = false;
      this.gateSubmit(true);
      // The design is on the dock as well as in the gallery, so on mobile it is
      // already on screen; bring the dock in if the gallery has scrolled away.
      // Deliberately no page scroll — see the dock methods above.
      this.updateDock();
      // A fresh design supersedes any earlier proof; clear the stale property.
      // The proof is baked fresh from the live design at add-to-cart (onSubmit).
      this.setProofProp('');
      // Hand this design on to whichever product the customer opens next.
      this.saveDesign();
      this.syncCourseUrl();
      ensureTitleFont().then(function () {
        this.renderTitle();
        // The wrap is only known once the title has been fitted, so the field is
        // brought into line with the design here rather than at the fill above.
        this.matchTitleFieldToDesign();
        // …and the carried copy travels with that same wrap.
        this.saveDesign();
      }.bind(this));
    }

    // ---- "About your preview" info box -------------------------------------
    // OpenStreetMap is the course catalogue, and its coverage is uneven: a course
    // can be missing some hole routing, its par, or have yardages measured off
    // the map rather than taken from the club's card. The API renders whatever
    // it has and describes the gaps in `completeness` (never refuses — see
    // generation-api/golfbroidery/completeness.py). We show those notices so the
    // customer knows the preview may be short of the finished piece, alongside
    // the standing promise that we complete it by hand before the order is made.

    applyCompleteness(state) {
      this.completeness = state && typeof state === 'object' ? state : null;
      this.setCompletenessProp();

      var notices = (this.completeness && this.completeness.notices) || [];
      // Belt and braces: an older API build returns no `completeness` at all,
      // and a course can be flagged incomplete with no sentence to show. Either
      // way, say nothing rather than show an empty box.
      if (!notices.length || this.isFullLayout()) {
        this.syncPreviewNotes();
        this.showCompleteNotice();
        return;
      }

      // Fresh field for a new course; the confirmation for one already asked for.
      this.syncNotifyForms();
      this.syncMappingRank();
      // Phone: the same fact under the pinned preview and above the buy button.
      this.syncPreviewNotes();

      if (!this.noticeEl) return;

      if (this.noticeListEl) {
        this.noticeListEl.innerHTML = '';
        notices.forEach(function (text) {
          if (!text) return;
          var li = document.createElement('li');
          li.className = 'course-designer__notice-item';
          li.textContent = text; // API copy, inserted as text — never as markup
          this.noticeListEl.appendChild(li);
        }.bind(this));
      }
      if (this.noticePartialEl) this.noticePartialEl.hidden = false;
      if (this.noticeCompleteEl) this.noticeCompleteEl.hidden = true;
      this.noticeEl.classList.add('course-designer__notice--attention');
      this.noticeEl.classList.remove('course-designer__notice--complete');
      this.noticeEl.hidden = false;
    }

    // Every hole routed and nothing printed on the design missing. A notice
    // that only says the yardages were measured off the map (`estimated`) is
    // not a gap in the layout: the box is summary-only, and it would otherwise
    // tell the customer that a fully mapped course isn't the full layout.
    // Stroke index isn't printed, so it doesn't count either. Fulfilment still
    // sees `estimated=yardage` in `_Course data` and checks the card.
    isFullLayout() {
      var c = this.completeness;
      if (!c || c.level !== 'complete') return false;
      return (c.missing || []).every(function (code) { return code === 'stroke_index'; });
    }

    // Nothing missing: a one-line confirmation, if the theme supplies the copy.
    showCompleteNotice() {
      if (!this.noticeEl) return;
      if (this.noticePartialEl) this.noticePartialEl.hidden = true;
      if (!this.noticeCompleteEl) { this.noticeEl.hidden = true; return; }
      this.noticeCompleteEl.hidden = false;
      this.noticeEl.classList.remove('course-designer__notice--attention');
      this.noticeEl.classList.add('course-designer__notice--complete');
      this.noticeEl.hidden = false;
    }

    // ---- "Finished by hand" -----------------------------------------------
    // The information box sits under Design It, and Add to cart is three blocks
    // further down (colour, size, quantity). On a laptop the box can be off
    // screen by the time the customer clicks; on a phone the sticky buy bar
    // means it may never cross the screen at all. So the same fact rides along
    // where the decision is made: one line above Add to cart (and above the
    // phone's sticky bar), and on a phone one under the pinned dock preview —
    // tap it to reach the box and its email sign-up.
    //
    // Never in front of the design. An incomplete preview is orderable, and
    // the moment it lands is the one the customer came for; this used to be a
    // modal over it, which read as an error at exactly the wrong time.
    //
    // Each line is rendered only when the store owner has left its copy in
    // place; a missing one is simply skipped.

    isPartialPreview() {
      var c = this.completeness;
      return !!(c && c.notices && c.notices.length) && !this.isFullLayout();
    }

    syncPreviewNotes() {
      var partial = this.isPartialPreview();
      var dockNote = this.dockEl ? this.dockEl.querySelector('[data-ref="dock-note"]') : null;
      if (dockNote && dockNote.hidden === partial) {
        dockNote.hidden = !partial;
        // The dock just changed height; keep the scroll padding under it honest.
        this.updateDock();
      }
      // Above the real Add to cart, at every width. Looked up fresh each time: a
      // colour or size change re-renders it, hidden.
      document.querySelectorAll('[data-ref="buy-note"]').forEach(function (el) {
        el.hidden = !partial;
      });
      var barNote = this.buyBarEl ? this.buyBarEl.querySelector('[data-ref="buy-bar-note"]') : null;
      if (barNote && barNote.hidden === partial) {
        barNote.hidden = !partial;
        // Re-measures the bar, so the page's bottom padding grows with it.
        this.syncBuyBar();
      }
    }

    // The dock note's one action: bring the information box (and the email
    // field in it) into view. Centred, so it clears the dock above and the buy
    // bar below.
    showPreviewNotice() {
      if (!this.noticeEl || this.noticeEl.hidden) return;
      this.noticeEl.scrollIntoView({
        behavior: reducedMotion() ? 'auto' : 'smooth',
        block: 'center',
      });
    }

    clearCompleteness() {
      this.completeness = null;
      this.setCompletenessProp();
      this.syncPreviewNotes();
      if (!this.noticeEl) return;
      this.noticeEl.hidden = true;
      this.noticeEl.classList.remove(
        'course-designer__notice--attention',
        'course-designer__notice--complete'
      );
      if (this.noticePartialEl) this.noticePartialEl.hidden = true;
      if (this.noticeCompleteEl) this.noticeCompleteEl.hidden = true;
      if (this.noticeListEl) this.noticeListEl.innerHTML = '';
    }

    // Flatten the completeness payload into one line-item property, so the order
    // tells fulfillment which designs need finishing by hand before they're made.
    // Empty (and so omitted from the cart) when the course rendered complete.
    completenessProp() {
      var c = this.completeness;
      if (!c || (!c.enrich_on_order && c.level === 'complete')) return '';
      var parts = ['level=' + (c.level || 'unknown')];
      if (c.missing && c.missing.length) parts.push('missing=' + c.missing.join(','));
      if (c.estimated && c.estimated.length) parts.push('estimated=' + c.estimated.join(','));
      if (c.missing_holes && c.missing_holes.length) {
        parts.push('unrouted_holes=' + c.missing_holes.join(','));
      }
      // Routed, but drawn without a fairway, green or tee (see `missing`).
      if (c.incomplete_holes && c.incomplete_holes.length) {
        parts.push('unfinished_holes=' + c.incomplete_holes.join(','));
      }
      if (c.holes_expected) {
        parts.push('holes=' + (c.holes_rendered || 0) + '/' + c.holes_expected);
      }
      return parts.join('; ');
    }

    setCompletenessProp() {
      if (this.propCompleteness) this.propCompleteness.value = this.completenessProp();
    }

    // ---- "Tell me when it's mapped" ------------------------------------------
    // An unfinished preview is a customer telling us exactly which course they
    // want. One email field (course-notify-form.liquid) turns that into a
    // customer tagged with the course, on the mailing list. Every
    // form[data-course-notify] on the page is the same sign-up: the information
    // box and, on a phone, the pop-up over the design.

    notifyForms() {
      return Array.prototype.slice.call(document.querySelectorAll('form[data-course-notify]'));
    }

    bindNotifyForms() {
      this._notified = {};
      var remembered = '';
      try { remembered = window.localStorage.getItem(NOTIFY_EMAIL_KEY) || ''; } catch (e) {}
      this.notifyForms().forEach(function (form) {
        var email = form.querySelector('[data-ref="notify-email"]');
        if (email && remembered && !email.value) email.value = remembered;
        form.addEventListener('submit', this.onNotifySubmit.bind(this));
      }.bind(this));
    }

    // Which course a sign-up is for: ids when the autocomplete supplied them,
    // the typed name when it didn't.
    notifyKey() {
      var typed = this.searchEl ? this.searchEl.value.trim().toLowerCase() : '';
      return this.designKey() + '|' + typed;
    }

    // `waitlist: <course> [<id>]` — readable in the admin, searchable by
    // whoever finishes the mapping, and unique per course via the id.
    //
    // The name is the design's own (`_defaultTitle`, what the API's design log
    // calls the course) rather than what was typed, because the API reads these
    // tags back to count sign-ups towards the mapping rank and matches on it.
    // A long name is trimmed, never the bracketed ids after it: those are the
    // API's fallback when the name doesn't match.
    notifyCourseTag() {
      var typed = this.searchEl ? this.searchEl.value.trim() : '';
      var name = (this._defaultTitle || typed).replace(/,/g, ' ');
      var ref = this.selectedOsmId != null ? 'osm ' + this.selectedOsmId
        : this.selectedCourseId != null ? 'id ' + this.selectedCourseId : '';
      if (this.selectedRouting != null && ref) ref += ' r' + this.selectedRouting;
      var suffix = ref ? ' [' + ref + ']' : '';
      var room = NOTIFY_TAG_MAX - 'waitlist: '.length - suffix.length;
      return ('waitlist: ' + name.slice(0, Math.max(room, 0)).trim() + suffix).trim();
    }

    // "This course is #4 in the mapping queue": where this course ranks among
    // the ones customers have designed that still need mapping by hand, by how
    // often they're searched (/api/mapping-rank, from the API's design log).
    // A rank rather than the queue's length, because a rank is something people
    // can move — the share prompt below says how. The formula (weights and
    // window) comes from the block's settings on the line itself, so it can be
    // changed in the theme editor without an API release. Asked once per
    // course, and the line stays hidden unless a rank comes back — the prompt
    // reads whole without it.
    syncMappingRank() {
      var lines = document.querySelectorAll('[data-ref="notify-rank"]');
      if (!lines.length || !this.apiBase) return;
      // Deferred a tick: a fresh render applies completeness before it records
      // the course's name (`_defaultTitle`), which is what the log counts by.
      Promise.resolve().then(function () {
        var course = this._defaultTitle || '';
        if (this._rankFor === course) return;
        this._rankFor = course;
        lines.forEach(function (el) { el.hidden = true; });
        var d = lines[0].dataset;
        var query = new URLSearchParams({
          course: course,
          searches: d.wSearches || '1',
          people: d.wPeople || '0',
          carts: d.wCarts || '0',
          signups: d.wSignups || '0',
          days: String(parseInt(d.days, 10) || 0),
        });
        return fetch(this.apiBase + '/api/mapping-rank?' + query.toString())
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (this._rankFor !== course) return; // the customer moved on
            var rank = data && data.rank;
            if (typeof rank !== 'number' || rank < 1 || !data.searches) return;
            lines.forEach(function (el) {
              var template = el.dataset.template;
              if (!template) return;
              // A shared rank reads as a plain "#4": the tie is ours to know.
              // The rank goes in bold, as one figure. Built as nodes: the
              // template is merchant copy and stays text.
              el.textContent = '';
              template.split(/(\[rank\])/).forEach(function (part) {
                if (part === '[rank]') {
                  var strong = document.createElement('strong');
                  strong.textContent = '#' + rank.toLocaleString();
                  el.append(strong);
                } else if (part) {
                  el.append(part);
                }
              });
              el.append(' ');
              el.hidden = false;
            });
          }.bind(this));
      }.bind(this)).catch(function () { /* no API, no rank: the prompt stands alone */ });
    }

    // Everything applyIncomingCourse needs to design this course again: the
    // share link and the sign-up's round trip both carry it.
    courseLinkFields() {
      var item = this._selectedItem || {};
      return {
        course: this.searchEl ? this.searchEl.value.trim() : '',
        course_id: this.selectedCourseId,
        osm_id: this.selectedOsmId,
        routing: this.selectedRouting,
        club: item.operator || '',
        place: item.place || '',
      };
    }

    // "Share this course": a link that opens this page on this course. It
    // goes to the device's share sheet where there is one (phones, mostly) and
    // to the clipboard where there isn't. Whoever follows it designs the course
    // on arrival, which is itself a search, so sharing is what moves the rank.
    bindShareButtons() {
      document.querySelectorAll('[data-ref="notify-share"]').forEach(function (button) {
        button.addEventListener('click', this.onShareClick.bind(this, button));
      }.bind(this));
    }

    onShareClick(button) {
      var fields = this.courseLinkFields();
      var params = this.courseLinkParams();
      var url = window.location.origin + window.location.pathname +
        (params.toString() ? '?' + params.toString() : '');
      var name = this._defaultTitle || fields.course;
      var text = (button.dataset.message || '').replace(/\[course\]/g, name);

      if (navigator.share) {
        navigator.share({ title: name, text: text, url: url })
          .catch(function () { /* dismissed: nothing to do */ });
        return;
      }
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(text ? text + ' ' + url : url).then(function () {
        var copied = button.dataset.copied;
        if (!copied) return;
        // Swap the words only, so the icon stays put.
        var label = button.querySelector('[data-ref="notify-share-label"]') || button;
        if (!button.dataset.label) button.dataset.label = label.textContent;
        label.textContent = copied;
        clearTimeout(button._copiedTimer);
        button._copiedTimer = setTimeout(function () {
          label.textContent = button.dataset.label;
        }, 2000);
      }).catch(function () { /* clipboard refused: leave the button as it was */ });
    }

    // Show the field, or the confirmation if this course has been asked for.
    syncNotifyForms() {
      var done = !!this._notified[this.notifyKey()];
      document.querySelectorAll('[data-ref="notify"]').forEach(function (box) {
        var wrap = box.querySelector('[data-ref="notify-form-wrap"]');
        var success = box.querySelector('[data-ref="notify-success"]');
        var error = box.querySelector('[data-ref="notify-error"]');
        if (wrap) wrap.hidden = done;
        if (success) success.hidden = !done;
        if (error) error.hidden = true;
      });
      // A native post that came back without landing: say so once, now that
      // the box is back on screen with the design.
      if (this._notifyFailed && !done) {
        this._notifyFailed = false;
        this.showNotifyError();
      }
    }

    showNotifyError() {
      document.querySelectorAll('[data-ref="notify-error"]').forEach(function (el) {
        el.hidden = false;
      });
    }

    onNotifySubmit(event) {
      // The native-post fallback below re-submits via requestSubmit(), which
      // fires this handler again — let that one through.
      if (this._notifyNativePass) return;
      var form = event.currentTarget;

      var tags = form.querySelector('[data-ref="notify-tags"]');
      if (tags) {
        var base = tags.getAttribute('value') || '';
        var courseTag = this.notifyCourseTag();
        tags.value = courseTag && courseTag !== 'waitlist:' ? base + ', ' + courseTag : base;
      }
      var email = form.querySelector('[data-ref="notify-email"]');
      if (email && email.value) {
        try { window.localStorage.setItem(NOTIFY_EMAIL_KEY, email.value.trim()); } catch (e) {}
      }

      var key = this.notifyKey();
      this.stashNotifyPending(key);

      // Spam protection owns this submit when it has bound the form (see
      // onReportSubmit): step aside and let the native post run. The stash
      // above brings the design back afterwards.
      if (form.dataset.hcaptchaBound || form.dataset.recaptchaBound || form.dataset.cptcha) {
        return;
      }
      event.preventDefault();

      var submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      document.querySelectorAll('[data-ref="notify-error"]').forEach(function (el) { el.hidden = true; });

      var native = function () {
        this._notifyNativePass = true;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else HTMLFormElement.prototype.submit.call(form);
      }.bind(this);

      fetch(form.getAttribute('action') || '/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          Accept: 'text/html',
        },
        body: new URLSearchParams(new FormData(form)).toString(),
      })
        .then(function (response) {
          var url = response.url || '';
          // Shopify redirects a customer post that worked to ?customer_posted=true.
          if (response.ok && url.indexOf('customer_posted=true') !== -1) {
            this.clearNotifyPending();
            this._notified[key] = true;
            this.syncNotifyForms();
            return;
          }
          // Captcha wanted (400 / /challenge) or the password wall swallowed
          // it: only a real submit gets through. Anything else came back
          // with the form's own errors — most likely the address.
          if (response.status === 400 || url.indexOf('/challenge') !== -1 || url.indexOf('/password') !== -1) {
            native();
            return;
          }
          this.clearNotifyPending();
          this.showNotifyError();
        }.bind(this))
        .catch(function () {
          this.clearNotifyPending();
          this.showNotifyError();
        }.bind(this))
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
    }

    // Everything applyIncomingCourse needs to design this course again.
    stashNotifyPending(key) {
      var pending = Object.assign({ key: key, at: Date.now() }, this.courseLinkFields());
      try { window.sessionStorage.setItem(NOTIFY_PENDING_KEY, JSON.stringify(pending)); } catch (e) {}
    }

    clearNotifyPending() {
      try { window.sessionStorage.removeItem(NOTIFY_PENDING_KEY); } catch (e) {}
    }

    // After a native post the page reloads with nothing on it. Write the course
    // back into the query string — applyIncomingCourse runs next and designs
    // it — and remember whether the sign-up landed, so the confirmation (or
    // the error) is waiting when the preview comes back.
    restoreNotifyState() {
      var pending = null;
      try { pending = JSON.parse(window.sessionStorage.getItem(NOTIFY_PENDING_KEY) || 'null'); } catch (e) {}
      if (!pending || !pending.key) return;
      this.clearNotifyPending();
      // A captcha page abandoned and the tab come back to later is not a
      // sign-up still in flight.
      if (!pending.at || Date.now() - pending.at > NOTIFY_PENDING_TTL_MS) return;

      var params;
      try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
      var posted = params.get('customer_posted') === 'true';
      if (posted) this._notified[pending.key] = true;
      else this._notifyFailed = true;

      // A course already in the URL is a newer choice than the stash.
      if (!params.get('course') && pending.course && window.history && window.history.replaceState) {
        params.set('course', pending.course);
        [['course_id', pending.course_id], ['osm_id', pending.osm_id], ['routing', pending.routing],
          ['club', pending.club], ['place', pending.place]].forEach(function (pair) {
          if (pair[1] != null && pair[1] !== '') params.set(pair[0], String(pair[1]));
        });
        params.delete('customer_posted');
        window.history.replaceState({}, '', window.location.pathname + '?' + params.toString());
      }
    }

    // Recolour the existing design's map to the ink that contrasts the newly
    // selected garment colour. With no design yet it just remembers the colour
    // for the next generate. Normally an instant client-side swap between the
    // pre-rendered variants (no round-trip). Only if the variant is missing —
    // e.g. a design generated before this code shipped — does it fall back to the
    // cached-token render, and then to a full regenerate if the token expired.
    recolor(ink) {
      this.courseInk = ink;
      if (this.propCourseInk) this.propCourseInk.value = ink;

      var variant = this.variants && this.variants[ink];
      if (variant && variant.src) {
        this.currentMapSrc = variant.src;
        this.mapImgs().forEach(function (img) { img.src = variant.src; });
        if (this.titleCtx) this.titleCtx.ink = variant.title_ink;
        this.setOverviewBg(ink);
        ensureTitleFont().then(function () { this.renderTitle(); }.bind(this));
        return;
      }

      if (this._hasDesign !== true || !this.designToken) return;

      fetch(this.apiBase + '/api/course-render', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: this.designToken, title_font: this.titleFont, color: ink }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          this.applyMap(data);
          ensureTitleFont().then(function () { this.renderTitle(); }.bind(this));
        }.bind(this))
        .catch(function () {
          // Token expired (cache evicted): regenerate from scratch if we still
          // have a course to render. Silent — the colour is already applied —
          // and the customer's title is carried across the re-render, since
          // nothing about the design they made has changed.
          if (this.searchEl.value.trim().length < 2) return;
          this._pendingTitle = this.titleEl ? this.titleEl.value : '';
          this.onGenerate();
        }.bind(this));
    }

    // ---- Live title overlay ----------------------------------------------
    // Mirror golfbroidery.course.fit_title_size: scale the title to fill a fixed
    // box, then stack its lines about the band centre. See paintTitle.
    renderTitle() {
      if (!this.titleCtx) return;
      this.titleSvgs().forEach(function (svg) { this.paintTitle(svg); }.bind(this));
      // The satellite panel's drawing is white whatever the garment, and only
      // carries the customer's title once their own satellite cut is showing
      // (the example image has its title baked in).
      var sat = this.satelliteTitleSvg;
      if (sat && this._satelliteShown) {
        sat.setAttribute('viewBox', '0 0 ' + this.titleCtx.canvasW + ' ' + this.titleCtx.canvasH);
        this.paintTitle(sat, '#fff');
      }
    }

    // Paint the (wrapped, fitted) title into one SVG. Both the overlay and the
    // overview slide share the same title box + canvas viewBox, so the same paint
    // lands identically on each. Mirrors (the upsell, the coming-soon carousel)
    // can pass an ink override to paint for a garment colour other than the
    // designer's current one.
    //
    // Mirrors golfbroidery.course.fit_title_size: the box is a fixed size and the
    // title is scaled to fill it — growing as well as shrinking — bounded by the
    // box width, the band height shared between the lines, and the em cap.
    paintTitle(svg, ink) {
      var box = this.titleCtx.box;
      var text = this.titleEl.value || '';
      var availW = Math.max(1, box.right_mm - box.left_mm);
      var bandH = box.band_h_mm || 0;
      var cap = box.height_mm;
      // Mirror the anchor the API laid the box out with: "end" (right-aligned to
      // the gutter beside the scorecard) or "start" (left-aligned from the map's
      // left edge — the whole-club view, which has no scorecard).
      var anchor = box.align === 'start' ? 'start' : 'end';
      var anchorX = anchor === 'start' ? box.left_mm : box.right_mm;

      svg.innerHTML = '';

      var make = function (line, size) {
        var t = document.createElementNS(SVGNS, 'text');
        t.setAttribute('text-anchor', anchor);
        t.setAttribute('x', anchorX);
        t.setAttribute('fill', ink || this.titleCtx.ink);
        t.setAttribute('font-family', FONT_FACE.family);
        t.setAttribute('font-weight', FONT_FACE.weight);
        t.setAttribute('font-style', FONT_FACE.style);
        t.setAttribute('dominant-baseline', 'central');
        t.setAttribute('font-size', size);
        // The Dawn theme sets letter-spacing on headings and .text-body (base.css),
        // and letter-spacing inherits in CSS — it leaks into this overlay's glyphs
        // and spreads the (connecting-script) title far wider than the design the
        // API bakes. The API renders the title from glyph outlines at their native
        // advance widths (golfbroidery/textpath.py); reset spacing to 'normal' so
        // the live overlay tracks like the baked download instead of the theme text.
        t.style.letterSpacing = 'normal';
        t.style.wordSpacing = 'normal';
        t.textContent = line;
        return t;
      }.bind(this);

      // Text width is linear in font-size, so one measurement at a probe size
      // gives the width per 1mm of em. Measured on the shared surface, never in
      // `svg` — see measuringSvg(); a hidden overlay measures 0.
      var PROBE = 10;
      var perEm = function (lines) {
        var host = measuringSvg();
        var widest = 0;
        var probes = lines.map(function (l) { var t = make(l, PROBE); host.appendChild(t); return t; });
        probes.forEach(function (t) { widest = Math.max(widest, t.getComputedTextLength()); });
        probes.forEach(function (t) { host.removeChild(t); });
        return widest / PROBE;
      };
      var emFor = function (lines) {
        var p = perEm(lines);
        var em = cap;
        if (p > 0) em = Math.min(em, availW / p);
        if (bandH) em = Math.min(em, bandH / Math.max(1, lines.length));
        return em;
      };

      var lines = titleLines(text);
      var size = emFor(lines);
      // The opportunistic third line: only when the customer hasn't placed their
      // own breaks, only when two lines came out cramped, and only when splitting
      // the long line actually buys size (it doesn't when the *other* line is the
      // one binding). Break at the same last-two-words boundary rather than
      // wherever measures widest, so the split follows the name.
      if (!hasExplicitBreaks(text) && lines.length === 2 && size < cap * THIRD_LINE_FLOOR) {
        var head = wrapLastTwo(lines[0]);
        if (head.length === 2) {
          var three = head.concat(lines.slice(1));
          var size3 = emFor(three);
          if (size3 > size) { lines = three; size = size3; }
        }
      }

      var nodes = lines.map(function (line) { var t = make(line, size); svg.appendChild(t); return t; });
      var lineGap = size * LINE_GAP_FACTOR;
      var n = lines.length;
      nodes.forEach(function (t, i) {
        t.setAttribute('y', box.center_y_mm + (i - (n - 1) / 2) * 2 * lineGap);
      });
      // Hand the wrap back so the title field can be seeded with the very lines
      // the design is drawn with — see matchTitleFieldToDesign.
      this._lastTitleFit = { lines: lines, size: size };
      return this._lastTitleFit;
    }

    // Rewrite the title field so it holds the break the design actually has.
    //
    // The automatic wrap happens at paint time, so a freshly generated design
    // reads "Vale do Lobo Ocean Course" in the field while the garment shows it
    // over two lines. Seeding the field with the fitted lines makes the field
    // and the design agree, and makes the break editable — it lands in the box
    // as the customer's own line break, which is exactly what it looks like.
    //
    // Safe to do because the lines being written are the ones the fit just
    // chose: re-reading them as explicit breaks reproduces the same wrap and the
    // same em, so nothing re-flows. Only ever called on a fresh design, never
    // while the customer is typing.
    matchTitleFieldToDesign() {
      var fit = this._lastTitleFit;
      if (!this.titleEl || !fit || fit.lines.length < 2) return;
      var wrapped = fit.lines.join('\n');
      if (this.titleEl.value === wrapped) return;
      this.titleEl.value = wrapped;
      if (this.propTitle) this.propTitle.value = wrapped;
      this.growTitleField();
    }

    // One-tap alternatives to typing a title on a phone keyboard. Built from
    // what the course already told us — the resolved name, that name with this
    // year on it, the club where OSM calls the course something else, the town
    // — so there is no list of guesses to keep and nothing here to translate.
    // Free text stays for anyone who wants it.
    //
    // The resolved name is one of the options, not the one option missing from
    // them. Leaving it out meant a customer who took an alternative had no way
    // back to the name they started with, and left the row a suggestion shorter
    // than it should have been.
    renderTitleChips(defaultTitle) {
      if (!this.titleChipsEl || !this.titleSuggestionsEl) return;
      this.titleChipsEl.innerHTML = '';

      var base = (defaultTitle || '').trim();
      var item = this._selectedItem;
      var club = item && item.operator ? String(item.operator).trim() : '';
      var place = item ? coursePlaceOf(item) : '';

      var options = [];
      if (base) options.push(base);
      if (base) options.push(base + ' ' + new Date().getFullYear());
      if (club) options.push(club);
      if (base && place) options.push(base + ', ' + place);

      var seen = {};
      this._titleOptions = options.filter(function (t) {
        t = (t || '').trim();
        if (!t || seen[t]) return false;
        seen[t] = true;
        return true;
      });

      // One option is the name that is already in the field: a row of one is a
      // statement, not a choice.
      if (this._titleOptions.length < 2) {
        this.titleSuggestionsEl.hidden = true;
        return;
      }

      this._titleOptions.forEach(function (text) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'course-designer__chip';
        chip.textContent = text;
        chip.addEventListener('click', function () {
          this.titleEl.value = text;
          // Straight through the ordinary edit path, so the clamp, the field's
          // growth, the line-item property and the live repaint all happen
          // exactly as they do for a typed character.
          this.onTitleInput();
          this.matchTitleFieldToDesign();
        }.bind(this));
        this.titleChipsEl.appendChild(chip);
      }.bind(this));

      this.titleSuggestionsEl.hidden = false;
      this.markSelectedTitleChip();
    }

    // Which chip the field is currently showing. Compared on collapsed
    // whitespace because the field is re-seeded with the design's own line
    // breaks once the title has been fitted (matchTitleFieldToDesign), so the
    // value carries newlines the chip's text does not. A hand-typed title
    // matches nothing and simply leaves the row unmarked.
    markSelectedTitleChip() {
      if (!this.titleChipsEl) return;
      var flat = (this.titleEl.value || '').replace(/\s+/g, ' ').trim();
      Array.prototype.forEach.call(
        this.titleChipsEl.children,
        function (chip) {
          var match = (chip.textContent || '').replace(/\s+/g, ' ').trim() === flat;
          chip.classList.toggle('is-selected', match);
          chip.setAttribute('aria-pressed', match ? 'true' : 'false');
        }
      );
    }

    onTitleInput() {
      this.clampTitleLines();
      this.growTitleField();
      this.propTitle.value = this.titleEl.value;
      this.renderTitle();
      // Typing over a suggestion drops its mark; typing one back out by hand
      // picks it up again.
      this.markSelectedTitleChip();
      // Their edit is part of the design, so it travels with it.
      this.saveDesign();
      // The title is baked into the flattened proof, but only at add-to-cart
      // (onSubmit bakes fresh from the live title), so there's nothing to warm.
    }

    // Enter breaks the line, but the band the lines share is fixed, so a fifth
    // line would be under 5mm tall — unreadable at any size the design allows.
    // Drop the excess here rather than in the renderer, so what the customer sees
    // in the field is what the design will carry. Mirrors TITLE_MAX_LINES.
    clampTitleLines() {
      var el = this.titleEl;
      var parts = el.value.replace(/\r\n?/g, '\n').split('\n');
      if (parts.length <= MAX_TITLE_LINES) return;
      var end = el.selectionEnd;
      el.value = parts.slice(0, MAX_TITLE_LINES).join('\n');
      try { el.setSelectionRange(Math.min(end, el.value.length), Math.min(end, el.value.length)); }
      catch (e) { /* selection APIs are unavailable on some mobile keyboards */ }
    }

    // The field is a textarea for the sake of Enter, so it has to grow itself —
    // a fixed height would either clip a three-line title or leave a gap under a
    // one-line one.
    growTitleField() {
      var el = this.titleEl;
      if (!el) return;
      el.style.height = 'auto';
      // scrollHeight always includes the padding, but these fields are
      // content-box (matching the search input beside them), where `height` is
      // the content alone — so assigning scrollHeight straight across leaves the
      // box one padding taller than its text every time.
      var cs = window.getComputedStyle(el);
      var pad = cs.boxSizing === 'content-box'
        ? parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
        : 0;
      el.style.height = Math.max(0, el.scrollHeight - pad) + 'px';
    }


    // ---- Cart proof (flattened garment + titled map) ----------------------
    // The product page shows the design as two layers (map <img> + live title
    // SVG) over the photo. The cart only keeps the bare product image, so we
    // bake a single flat proof — the titled map composited onto the garment —
    // server-side and store its URL as a line-item property the cart renders.

    // The currently displayed product photo (the image the overlay sits on).
    // Read live so a colour swap (which replaces the gallery) is picked up.
    //
    // Shopify renders product images as origin-relative `/cdn/shop/…` URLs, so
    // the browser resolves them against the current origin — which is the
    // `shopify theme dev` proxy (http://127.0.0.1:9292) in dev and a custom
    // domain in prod. The proof API only fetches from the Shopify CDN, so we
    // rebuild the URL on the shop's permanent *.myshopify.com domain (always
    // CDN-served + allow-listed), which works identically in dev and prod.
    garmentUrl() {
      var wrapper = this.overlayEl && this.overlayEl.closest('.product__media-wrapper');
      if (!wrapper) return '';
      // Exclude the overview slide's own map image — only the real garment photo
      // is a valid proof base. Lifestyle photos are excluded too: on wide screens
      // media-gallery.js lifts them into a column placed *before* the renders, so
      // a plain document-order match would pick a lifestyle shot.
      var img = wrapper.querySelector(
        '.product__media-item:not(.product__media-item--lifestyle) .product__media img:not(.course-overview__map)'
      );
      var src = img && (img.currentSrc || img.src);
      if (!src) return '';
      var abs = new URL(src, window.location.href);
      // Drop the srcset-chosen `width` (varies with viewport/DPR) so the proof
      // signature — and the server's dedup key — stay stable for one garment.
      abs.searchParams.delete('width');
      var pathq = abs.pathname + abs.search;
      var shop = this.dataset.shopDomain;
      return shop ? 'https://' + shop + pathq : abs.origin + pathq;
    }

    setProofProp(url) {
      if (this.propProof) this.propProof.value = url || '';
    }

    // Mirror the title-box geometry into its line-item property as
    // "left,right,cy,h,canvasW,canvasH,align,bandH" (canvas mm) — the cart bake's
    // title placement. The two trailing fields are appended rather than inserted
    // so the six numbers a pre-existing line item stored still parse.
    // Cleared (empty) when there's no design.
    setGeomProp() {
      if (!this.propGeom) return;
      var c = this.titleCtx;
      if (!c || !c.box) { this.propGeom.value = ''; return; }
      this.propGeom.value = [
        c.box.left_mm, c.box.right_mm, c.box.center_y_mm, c.box.height_mm,
        c.canvasW, c.canvasH, c.box.align === 'start' ? 'start' : 'end',
        c.box.band_h_mm || '',
      ].join(',');
    }

    // Bake a flattened proof (titled map composited onto a garment) for the
    // current design and resolve to its hosted URL. Shared by the main add-to-cart
    // (current garment) and the upsell (a sibling garment), so it takes the garment
    // + ink + placement explicitly. No caching and no shared-state side effects, so
    // the baked image always matches the design at call time. Resolves to null when
    // there's nothing to bake (no design / no garment) so callers fall back cleanly.
    bakeProofForGarment(opts) {
      opts = opts || {};
      if (this._hasDesign !== true) return Promise.resolve(null);
      var garment = opts.garmentUrl;
      if (!garment) return Promise.resolve(null);

      // Garment + placement, shared by both bakes.
      var place = {
        color: opts.color || this.courseInk,
        garment_url: garment,
        overlay_top_pct: opts.overlayTopPct != null ? opts.overlayTopPct : this.overlayTopPct,
        overlay_left_pct: opts.overlayLeftPct != null ? opts.overlayLeftPct : this.overlayLeftPct,
        overlay_width_pct: opts.overlayWidthPct != null ? opts.overlayWidthPct : this.overlayWidthPct,
      };
      // A design carried from another product page has no live render token —
      // the page that held it is gone — so bake from the map instead.
      if (!this.designToken) return this.bakeProofFromMap(place);

      var body = Object.assign({
        token: this.designToken,
        title: this.titleEl ? this.titleEl.value : '',
        title_font: this.titleFont,
      }, place);
      return fetch(this.apiBase + '/api/course-proof', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        // Resolve relative URLs (local-dev /api/files fallback) against the API
        // origin; hosted R2 URLs are absolute and pass through unchanged.
        .then(function (data) { return this.resolveUrl(data.url); }.bind(this))
        // An expired token (the render cache has evicted it) is not a failed
        // sale: the same proof can be composited from the hosted map.
        .catch(function () { return this.bakeProofFromMap(place); }.bind(this));
    }

    // Token-free proof: composite the hosted map + the live title, laid out from
    // the stored title-box geometry, onto the garment. Same endpoint the cart
    // upsell bakes with (see course-upsell.js), and the path every carried design
    // takes. Resolves to null when there's nothing to composite, so callers fall
    // back to the plain garment photo rather than blocking the add.
    bakeProofFromMap(place) {
      var variant = this.variants && this.variants[place.color];
      var ctx = this.titleCtx;
      if (!variant || !variant.url || !ctx || !ctx.box) return Promise.resolve(null);

      var body = Object.assign({
        map_url: variant.url,
        title: this.titleEl ? this.titleEl.value : '',
        title_font: this.titleFont,
        title_left_mm: ctx.box.left_mm,
        title_right_mm: ctx.box.right_mm,
        title_cy_mm: ctx.box.center_y_mm,
        title_h_mm: ctx.box.height_mm,
        canvas_w_mm: ctx.canvasW,
        canvas_h_mm: ctx.canvasH,
        title_align: ctx.box.align === 'start' ? 'start' : 'end',
      }, place);
      if (ctx.box.band_h_mm) body.title_band_h_mm = ctx.box.band_h_mm;

      return fetch(this.apiBase + '/api/course-proof-from-map', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) { return this.resolveUrl(data.url); }.bind(this))
        .catch(function () { return null; });
    }

    // Bake the proof for the CURRENT on-screen garment (used by add-to-cart).
    bakeProof() {
      return this.bakeProofForGarment({
        garmentUrl: this.garmentUrl(),
        color: this.courseInk,
        overlayTopPct: this.overlayTopPct,
        overlayLeftPct: this.overlayLeftPct,
        overlayWidthPct: this.overlayWidthPct,
      });
    }

    // ---- Public API for the upsell (<course-upsell>) ----------------------
    // Whether a real (non-placeholder) design currently exists.
    hasDesign() { return this._hasDesign === true; }

    // The design line-item properties to carry onto an upsell garment. The ink is
    // chosen per upsell garment by the caller, so it's not included here.
    designData() {
      return {
        course: this.propCourse ? this.propCourse.value : '',
        title: this.propTitle ? this.propTitle.value : '',
        courseId: this.propCourseId ? this.propCourseId.value : '',
        titleFont: this.titleFont,
        mapGreen: this.propMap.green ? this.propMap.green.value : '',
        mapCream: this.propMap.cream ? this.propMap.cream.value : '',
        geom: this.propGeom ? this.propGeom.value : '',
        // Same design, same gaps — an upsell garment carries the same note to
        // fulfillment as the main line.
        completeness: this.completenessProp(),
      };
    }

    // The map image to preview for a given ink: the generated variant once a design
    // exists, otherwise the placeholder art. Lets upsell cards mirror exactly what
    // the customer sees on the main product (placeholder until they generate).
    previewSrc(ink) {
      var variant = this.variants && this.variants[ink];
      if (this._hasDesign === true && variant && variant.src) return variant.src;
      return this.placeholderSrc(ink);
    }

    // Submit interceptor: bake a fresh proof from the current design and attach it
    // before the product form posts. Runs at document capture so it precedes
    // <product-form>. We always bake (never reuse a cached proof) so the proof can
    // never lag the on-screen title, and serialize on `_adding` so a rapid second
    // click can't post a half-baked or mismatched proof.
    onSubmit(evt) {
      var form = evt.target;
      // NB: read the id via getAttribute — `form.id` is shadowed by the variant
      // <input name="id"> (form controls expose themselves as named properties).
      if (!form || form.getAttribute('id') !== this.dataset.formId) return;
      // The resubmit we fire below: let it pass straight through.
      if (this._bypassProof) { this._bypassProof = false; return; }
      // No design to flatten → nothing to do. A carried design has no token but
      // still bakes (from its map), so only the design itself gates this.
      if (this._hasDesign !== true) return;

      // Block this submit so a half-ready design can't be added, then re-fire it
      // once the fresh proof is attached. On bake failure we clear the property so
      // the cart falls back to the plain product image rather than a stale/wrong
      // proof — the sale is never blocked (design data is captured regardless).
      evt.preventDefault();
      evt.stopImmediatePropagation();

      // Ignore extra clicks while an add is already in flight.
      if (this._adding) return;
      this._adding = true;

      // Keep the text metafield in lockstep with what we're about to bake.
      this.propTitle.value = this.titleEl.value;
      var btn = document.getElementById(this.dataset.submitId);
      var spinner = btn && btn.querySelector('.loading__spinner');
      if (btn) btn.setAttribute('aria-disabled', 'true');
      if (spinner) spinner.classList.remove('hidden');
      this.setStatus('Preparing your design…', '');
      this.bakeProof()
        .then(function (url) { this.setProofProp(url || ''); }.bind(this))
        .catch(function () { this.setProofProp(''); }.bind(this))
        .then(function () {
          this.setStatus('', '');
          if (spinner) spinner.classList.add('hidden');
          // Clear our gate so <product-form> proceeds; it re-sets aria-disabled
          // for the duration of its own cart-add fetch and guards its own re-entry.
          if (btn) btn.removeAttribute('aria-disabled');
          this._adding = false;
          this._bypassProof = true;
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        }.bind(this));
    }

    // ---- Helpers ----------------------------------------------------------
    // Is this failure "we don't have enough course data (yet)" rather than "a
    // thing broke"? Everything data-shaped — an unmapped course, a course we
    // can't resolve, holes with no OSM way, holes missing features — gets the
    // one apologetic modal + "request this course" CTA. The customer is never
    // told *which*, because there's nothing they'd do differently.
    //
    // The API flags these with detail.kind === 'course_data' (see
    // _course_data_error in generation-api/api.py), so a NEW data failure mode
    // can't silently fall through to "something went wrong". The code list is
    // the fallback for an API build that predates the flag.
    isCourseDataError(detail) {
      if (!detail || typeof detail !== 'object') return false;
      if (detail.kind === 'course_data') return true;
      return COURSE_DATA_CODES.indexOf(detail.error) !== -1;
    }

    onError(err) {
      // Clear any transient status ("Generating…"): the outcome of a failure is
      // always a modal, so raw error text can never shadow the course title.
      this.setStatus('', '');
      var detail = err && err.data && err.data.detail;
      var code = detail && typeof detail === 'object' ? detail.error : null;

      // Log the real cause for debugging either way; never render it.
      if (window.console && console.warn) {
        console.warn('[course-designer] generation failed:', code || (err && err.message) || err);
      }

      // Course-data gap → reassuring apology + request path.
      if (this.isCourseDataError(detail)) {
        this.showDataModal();
        return;
      }

      // Genuinely unrelated to course data — network, 5xx, Overpass being down,
      // a bug — gets the generic modal.
      this.showErrorModal();
    }

    // We gave up waiting; nothing is known to be broken. Kept apart from
    // onError because the honest thing to offer here is the same click again —
    // the render was probably queued behind other work and the next attempt
    // often lands straight away — not an apology and a report form.
    onTimeout() {
      this.setStatus('', '');
      if (window.console && console.warn) {
        console.warn('[course-designer] generation timed out after ' + GENERATE_TIMEOUT_MS + 'ms');
      }
      this.showTimeoutModal();
    }

    // "Taking longer than usual" modal + a retry. Falls back to the status line
    // if the theme has no such modal, so the customer is never left with a
    // spinner that has silently stopped — the Design It button is enabled again
    // behind it either way.
    showTimeoutModal() {
      var modal = document.getElementById('CourseTimeoutModal');
      if (modal && typeof modal.show === 'function') {
        modal.show(this.generateEl || this.searchEl);
        return;
      }
      this.setStatus(TIMEOUT_FALLBACK, 'error');
    }

    // Generic "something went wrong" modal, for failures that aren't about the
    // course data. Its contact button always opens the report form as a design
    // issue. Falls back to a plain, non-raw status line only if the modal isn't
    // on the page, so the customer is never left with no feedback.
    showErrorModal() {
      this._errorReportKind = 'generation_issue';
      var modal = document.getElementById('CourseErrorModal');
      if (modal && typeof modal.show === 'function') {
        modal.show(this.generateEl || this.searchEl);
        return;
      }
      this.setStatus('Something went wrong generating your preview. Please try again.', 'error');
    }

    // Apologetic "we don't have enough data for this course yet" modal — the
    // single customer-facing outcome for every course-data gap. The copy is
    // merchant-edited and deliberately says nothing about which holes or
    // features are missing.
    showDataModal() {
      var modal = document.getElementById('CourseDataModal');
      if (!modal || typeof modal.show !== 'function') {
        // Theme without the apology modal: a generic notice beats silence.
        this.showErrorModal();
        return;
      }
      modal.show(this.generateEl || this.searchEl);
    }

    setStatus(msg, kind) {
      this.statusEl.textContent = msg;
      this.statusEl.classList.toggle('course-designer__status--error', kind === 'error');
    }

    // Block Add to Cart until a design exists (so no order lacks a design).
    // The button is resolved fresh each call so it survives a colour swap, which
    // replaces the buy buttons (see the 'color-swatch:swapped' listener).
    gateSubmit(hasDesign) {
      this._hasDesign = hasDesign;
      // Let the upsell (and anything else) mirror the design state: enable its
      // add buttons and repaint its garment previews. Fired here because this is
      // the one chokepoint hit on connect, generate, and colour swap.
      document.dispatchEvent(new CustomEvent('course-designer:design-changed', {
        detail: { hasDesign: hasDesign === true },
      }));
      var submit = document.getElementById(this.dataset.submitId);
      if (submit && this.requireDesign) submit.disabled = !hasDesign;
      // After the gate, never before. The bar copies the real button's disabled
      // state, so syncing first copied the state the gate was about to change:
      // the first design enabled the button on the page and left the sticky bar
      // holding a dead one, which is the only add-to-cart most phone customers
      // ever see.
      this.syncBuyBar();
    }
  }

  if (!customElements.get('course-designer')) {
    customElements.define('course-designer', CourseDesigner);
  }
})();
